import dayjs from 'dayjs'
import { pick } from 'lodash-es'
import omit from 'lodash-es/omit'
import demoPostSrc1 from '@/assets/images/demo/demo-post-1.jpg'
import demoPostSrc2 from '@/assets/images/demo/demo-post-2.jpg'
import demoPostSrc3 from '@/assets/images/demo/demo-post-3.jpg'
import demoPostSrc4 from '@/assets/images/demo/demo-post-4.jpg'
import demoPostSrc5 from '@/assets/images/demo/demo-post-5.jpg'
import demoPostSrc6 from '@/assets/images/demo/demo-post-6.jpg'
import demoPostSrc7 from '@/assets/images/demo/demo-post-7.jpg'
import demoPostSrc8 from '@/assets/images/demo/demo-post-8.jpg'
import demoPostSrc9 from '@/assets/images/demo/demo-post-9.jpg'
import demoPostSrc10 from '@/assets/images/demo/demo-post-10.jpg'
import {
  makeBrandInfo6,
  makeBrandInfo7,
  makeBrandInfo8,
  MOCK_KOL_INFO_PREFIX,
} from '@/constants/mock-data/kol-info'
import {
  makePostContent1,
  makePostContent10,
  makePostContent11,
  makePostContent2,
  makePostContent3,
  makePostContent4,
  makePostContent5,
  makePostContent6,
  makePostContent7,
  makePostContent8,
  makePostContent9,
} from '@/constants/mock-data/post'
import { SupportedLanguages } from '@/i18n/config'
import { KolIdentityType } from '@/types/api/search'
import {
  CompetitiveBrandAnalysisKolListChartsResponse,
  CompetitiveBrandAnalysisKolListEffectivenessResponse,
  CompetitiveBrandAnalysisKolListMetricChartsResponse,
  CompetitiveKolStatisticListCharts,
  CompetitiveKolStatisticListResponse,
} from '@/types/schema/api/competitive-brand-analyses'
import {
  CompetitiveMentionAnalysisKolAnalyzeListResponse,
  CompetitiveMentionTopTenPostList,
  FetchCompetitiveMentionEffectivenessTrendChartDataResponse,
  FetchCompetitiveMentionKolFollowerChartDataResponse,
  FetchCompetitiveMentionKolPropertyChartDataResponse,
  FetchCompetitiveMentionPostKolListResponse,
  FetchCompetitiveMentionSimilarKolListResponse,
} from '@/types/schema/api/competitive-mention-analyses'
import {
  CompetitiveAnalysisKol,
  CompetitiveAnalysisKolList,
} from '@/types/schema/competitive-analyses'
import {
  CompetitiveAnalysisCrawlerStatus,
  Hashtag,
} from '@/types/schema/competitive-analyses/competitive-brand-analyses'
import {
  CompetitiveKeywordAnalysis,
  CompetitiveKeywordAnalysisKeyword,
  CompetitiveKeywordAnalysisKeywordList,
  CompetitiveKeywordAnalysisStatisticList,
  CompetitiveKeywordEffectivenessTrendChartDetail,
  CompetitiveKeywordEffectivenessTrendList,
  CompetitiveKeywordTopTenPostList,
  FetchCompetitiveKeywordKolCustomizedTagsNameChartDataResponse,
  FetchCompetitiveKeywordKolFollowerChartDataResponse,
  FetchCompetitiveKeywordPostKolListResponse,
  type FetchMentionKeywordEstimatedCollaborationPriceChartDataResponse,
} from '@/types/schema/competitive-analyses/competitive-keyword-analyses'
import {
  CompetitiveMentionAnalysis,
  CompetitiveMentionEffectivenessTrendChartDetail,
  type FetchMentionKolEstimatedCollaborationPriceChartDataResponse,
} from '@/types/schema/competitive-analyses/competitive-mention-analyses'
import { PlatformShortcode } from '@/utils/convert-platform'
import { Potential } from '@/utils/detail/potential'

// 沒有假資料需求的屬性宣告在這邊
const unimportantAttrs = {
  has_target_platform: true,
}

const DEMO_COMPETITIVE_ANALYSIS_ID = -1

const DEMO_CRAWLER_STATUS: CompetitiveAnalysisCrawlerStatus = {
  crawler_job_status: null,
  is_triggerable: true,
}

const DEMO_COMPETITIVE_MENTION_ANALYSIS: CompetitiveMentionAnalysis = {
  id: DEMO_COMPETITIVE_ANALYSIS_ID,
  name: 'DEMO',
  competitive_kol_count: 3,
  updated_at: dayjs().format(),
  creator: {
    id: 0,
    name: 'Kolr',
  },
  latest_updated_at: dayjs().format(),
}

export const makeDemoCompetitiveBrandAnalysisKolListEffectiveness = (
  locale?: SupportedLanguages,
): CompetitiveBrandAnalysisKolListEffectivenessResponse => {
  const brandInfo6 = makeBrandInfo6(locale)
  const brandInfo7 = makeBrandInfo7(locale)
  const brandInfo8 = makeBrandInfo8(locale)

  return {
    kol_infos: [
      {
        ...unimportantAttrs,
        uuid: brandInfo8.id,
        name: brandInfo8.name,
        starred: false,
        trend_data: [
          {
            key: '2023-11-01',
            from: '2023-11-01T00:00:00Z',
            to: '2023-11-30T23:59:59.999999999Z',
            post_count: 2,
            engagement_count: 3739,
            engagement_rate: '0.03',
            view_rate: '14178.00',
            avg_like_count: '1844',
            avg_comment_count: '25.5',
            avg_view_count: '14178',
            avg_share_count: '0',
            avg_engagement_count: '1869.5',
            reels_engagement_rate: '0.03',
            value_evaluation: '144333.4',
          },
          {
            key: '2023-12-01',
            from: '2023-12-01T00:00:00Z',
            to: '2023-12-31T23:59:59.999999999Z',
            post_count: 24,
            engagement_count: 37861,
            engagement_rate: '0.02',
            view_rate: '8909.25',
            avg_like_count: '1549.625',
            avg_comment_count: '27.9166666666666667',
            avg_view_count: '8909.25',
            avg_share_count: '0',
            avg_engagement_count: '1577.5416666666666667',
            reels_engagement_rate: '0.02',
            value_evaluation: '1405998.6',
          },
          {
            key: '2024-01-01',
            from: '2024-01-01T00:00:00Z',
            to: '2024-01-31T23:59:59.999999999Z',
            post_count: 48,
            engagement_count: 86158,
            engagement_rate: '0.03',
            view_rate: '12667.88',
            avg_like_count: '1766.0416666666666667',
            avg_comment_count: '28.9166666666666667',
            avg_view_count: '12667.875',
            avg_share_count: '0',
            avg_engagement_count: '1794.9583333333333333',
            reels_engagement_rate: '0.03',
            value_evaluation: '3327680',
          },
          {
            key: '2024-02-01',
            from: '2024-02-01T00:00:00Z',
            to: '2024-02-29T23:59:59.999999999Z',
            post_count: 36,
            engagement_count: 110548,
            engagement_rate: '0.05',
            view_rate: '13937.44',
            avg_like_count: '3003.7777777777777778',
            avg_comment_count: '67',
            avg_view_count: '13937.4444444444444444',
            avg_share_count: '0',
            avg_engagement_count: '3070.7777777777777778',
            reels_engagement_rate: '0.05',
            value_evaluation: '4073491.2',
          },
          {
            key: '2024-03-01',
            from: '2024-03-01T00:00:00Z',
            to: '2024-03-31T23:59:59.999999999Z',
            post_count: 39,
            engagement_count: 66521,
            engagement_rate: '0.03',
            view_rate: '12360.69',
            avg_like_count: '1678.2307692307692308',
            avg_comment_count: '27.4358974358974359',
            avg_view_count: '12360.6923076923076923',
            avg_share_count: '0',
            avg_engagement_count: '1705.6666666666666667',
            reels_engagement_rate: '0.03',
            value_evaluation: '2586369.6',
          },
          {
            key: '2024-04-01',
            from: '2024-04-01T00:00:00Z',
            to: '2024-04-30T23:59:59.999999999Z',
            post_count: 21,
            engagement_count: 29746,
            engagement_rate: '0.02',
            view_rate: '11631.90',
            avg_like_count: '1392.8095238095238095',
            avg_comment_count: '23.6666666666666667',
            avg_view_count: '11631.9047619047619048',
            avg_share_count: '0',
            avg_engagement_count: '1416.4761904761904762',
            reels_engagement_rate: '0.02',
            value_evaluation: '1202412.4',
          },
          {
            key: '2024-05-01',
            from: '2024-05-01T00:00:00Z',
            to: '2024-05-31T23:59:59.999999999Z',
            post_count: 15,
            engagement_count: 27833,
            engagement_rate: '0.03',
            view_rate: '7863.67',
            avg_like_count: '1820.6',
            avg_comment_count: '34.9333333333333333',
            avg_view_count: '7863.6666666666666667',
            avg_share_count: '0',
            avg_engagement_count: '1855.5333333333333333',
            reels_engagement_rate: '0.03',
            value_evaluation: '988325.6',
          },
        ],
      },
      {
        ...unimportantAttrs,
        uuid: brandInfo7.id,
        name: brandInfo7.name,
        starred: false,
        trend_data: [
          {
            key: '2023-11-01',
            from: '2023-11-01T00:00:00Z',
            to: '2023-11-30T23:59:59.999999999Z',
            post_count: 1,
            engagement_count: 815,
            engagement_rate: '0.01698482781648636',
            view_rate: '0.43208152055740356',
            avg_like_count: '810',
            avg_comment_count: '5',
            avg_view_count: '20733',
            avg_share_count: '0',
            avg_engagement_count: '815',
            reels_engagement_rate: '0.016984828276092',
            value_evaluation: '49957.2',
          },
          {
            key: '2023-12-01',
            from: '2023-12-01T00:00:00Z',
            to: '2023-12-31T23:59:59.999999999Z',
            post_count: 3,
            engagement_count: 3515,
            engagement_rate: '0.02',
            view_rate: '0.00',
            avg_like_count: '1166.3333333333333333',
            avg_comment_count: '5.3333333333333333',
            avg_view_count: '0',
            avg_share_count: '0',
            avg_engagement_count: '1171.6666666666666667',
            reels_engagement_rate: '0.02',
            value_evaluation: '88565.8',
          },
          {
            key: '2024-01-01',
            from: '2024-01-01T00:00:00Z',
            to: '2024-01-31T23:59:59.999999999Z',
            post_count: 2,
            engagement_count: 2796,
            engagement_rate: '0.03',
            view_rate: '0.00',
            avg_like_count: '1395.5',
            avg_comment_count: '2.5',
            avg_view_count: '0',
            avg_share_count: '0',
            avg_engagement_count: '1398',
            reels_engagement_rate: '0.03',
            value_evaluation: '68078.8',
          },
          {
            key: '2024-02-01',
            from: '2024-02-01T00:00:00Z',
            to: '2024-02-29T23:59:59.999999999Z',
            post_count: 4,
            engagement_count: 3396,
            engagement_rate: '0.02',
            view_rate: '4946.75',
            avg_like_count: '845.25',
            avg_comment_count: '3.75',
            avg_view_count: '4946.75',
            avg_share_count: '0',
            avg_engagement_count: '849',
            reels_engagement_rate: '0.02',
            value_evaluation: '113128.6',
          },
          {
            key: '2024-03-01',
            from: '2024-03-01T00:00:00Z',
            to: '2024-03-31T23:59:59.999999999Z',
            post_count: 3,
            engagement_count: 2064,
            engagement_rate: '0.01',
            view_rate: '6717.67',
            avg_like_count: '685',
            avg_comment_count: '3',
            avg_view_count: '6717.6666666666666667',
            avg_share_count: '0',
            avg_engagement_count: '688',
            reels_engagement_rate: '0.01',
            value_evaluation: '80098.6',
          },
          {
            key: '2024-04-01',
            from: '2024-04-01T00:00:00Z',
            to: '2024-04-30T23:59:59.999999999Z',
            post_count: 1,
            engagement_count: 598,
            engagement_rate: '0.01',
            view_rate: '0.00',
            avg_like_count: '598',
            avg_comment_count: '0',
            avg_view_count: '0',
            avg_share_count: '0',
            avg_engagement_count: '598',
            reels_engagement_rate: '0.01',
            value_evaluation: '14232.4',
          },
          {
            key: '2024-05-01',
            from: '2024-05-01T00:00:00Z',
            to: '2024-05-31T23:59:59.999999999Z',
            post_count: 2,
            engagement_count: 1788,
            engagement_rate: '0.02',
            view_rate: '0.00',
            avg_like_count: '892.5',
            avg_comment_count: '1.5',
            avg_view_count: '0',
            avg_share_count: '0',
            avg_engagement_count: '894',
            reels_engagement_rate: '0.02',
            value_evaluation: '43474.8',
          },
        ],
      },
      {
        ...unimportantAttrs,
        uuid: brandInfo6.id,
        name: brandInfo6.name,
        starred: false,
        trend_data: [
          {
            key: '2023-11-01',
            from: '2023-11-01T00:00:00Z',
            to: '2023-11-30T23:59:59.999999999Z',
            post_count: 3,
            engagement_count: 4938,
            engagement_rate: '0.04',
            view_rate: '7548.00',
            avg_like_count: '1635.6666666666666667',
            avg_comment_count: '10.3333333333333333',
            avg_view_count: '7548',
            avg_share_count: '0',
            avg_engagement_count: '1646',
            reels_engagement_rate: '0.04',
            value_evaluation: '158736.8',
          },
          {
            key: '2023-12-01',
            from: '2023-12-01T00:00:00Z',
            to: '2023-12-31T23:59:59.999999999Z',
            post_count: 31,
            engagement_count: 38503,
            engagement_rate: '0.03',
            view_rate: '25655.74',
            avg_like_count: '1231.0967741935483871',
            avg_comment_count: '10.9354838709677419',
            avg_view_count: '25655.7419354838709677',
            avg_share_count: '0',
            avg_engagement_count: '1242.032258064516129',
            reels_engagement_rate: '0.03',
            value_evaluation: '2133835.8',
          },
          {
            key: '2024-01-01',
            from: '2024-01-01T00:00:00Z',
            to: '2024-01-31T23:59:59.999999999Z',
            post_count: 31,
            engagement_count: 45648,
            engagement_rate: '0.03',
            view_rate: '22011.45',
            avg_like_count: '1461.032258064516129',
            avg_comment_count: '11.4838709677419355',
            avg_view_count: '22011.4516129032258065',
            avg_share_count: '0',
            avg_engagement_count: '1472.5161290322580645',
            reels_engagement_rate: '0.03',
            value_evaluation: '2150940.2',
          },
          {
            key: '2024-02-01',
            from: '2024-02-01T00:00:00Z',
            to: '2024-02-29T23:59:59.999999999Z',
            post_count: 29,
            engagement_count: 39259,
            engagement_rate: '0.03',
            view_rate: '17367.93',
            avg_like_count: '1342.7586206896551724',
            avg_comment_count: '11',
            avg_view_count: '17367.9310344827586207',
            avg_share_count: '0',
            avg_engagement_count: '1353.7586206896551724',
            reels_engagement_rate: '0.03',
            value_evaluation: '1737371.4',
          },
          {
            key: '2024-03-01',
            from: '2024-03-01T00:00:00Z',
            to: '2024-03-31T23:59:59.999999999Z',
            post_count: 27,
            engagement_count: 39878,
            engagement_rate: '0.03',
            view_rate: '9332.74',
            avg_like_count: '1464.1851851851851852',
            avg_comment_count: '12.7777777777777778',
            avg_view_count: '9332.7407407407407407',
            avg_share_count: '0',
            avg_engagement_count: '1476.962962962962963',
            reels_engagement_rate: '0.03',
            value_evaluation: '1407720',
          },
          {
            key: '2024-04-01',
            from: '2024-04-01T00:00:00Z',
            to: '2024-04-30T23:59:59.999999999Z',
            post_count: 28,
            engagement_count: 45578,
            engagement_rate: '0.04',
            view_rate: '32419.11',
            avg_like_count: '1614.3928571428571429',
            avg_comment_count: '13.3928571428571429',
            avg_view_count: '32419.1071428571428571',
            avg_share_count: '0',
            avg_engagement_count: '1627.7857142857142857',
            reels_engagement_rate: '0.04',
            value_evaluation: '2470635.4',
          },
          {
            key: '2024-05-01',
            from: '2024-05-01T00:00:00Z',
            to: '2024-05-31T23:59:59.999999999Z',
            post_count: 21,
            engagement_count: 26396,
            engagement_rate: '0.03',
            view_rate: '21077.76',
            avg_like_count: '1244.7619047619047619',
            avg_comment_count: '12.1904761904761905',
            avg_view_count: '21077.7619047619047619',
            avg_share_count: '0',
            avg_engagement_count: '1256.9523809523809524',
            reels_engagement_rate: '0.03',
            value_evaluation: '1326451.8',
          },
        ],
      },
    ],
  }
}

export const makeDemoCompetitiveBrandAnalysisKolListCharts = (
  locale?: SupportedLanguages,
): CompetitiveBrandAnalysisKolListChartsResponse => {
  const brandInfo6 = makeBrandInfo6(locale)
  const brandInfo7 = makeBrandInfo7(locale)
  const brandInfo8 = makeBrandInfo8(locale)

  return {
    follower_count_charts: [
      {
        ...unimportantAttrs,
        uuid: brandInfo8.id,
        name: brandInfo8.name,
        starred_kol: false,
        follower_count: 65376,
      },
      {
        ...unimportantAttrs,
        uuid: brandInfo7.id,
        name: brandInfo7.name,
        starred_kol: false,
        follower_count: 49009,
      },
      {
        ...unimportantAttrs,
        uuid: brandInfo6.id,
        name: brandInfo6.name,
        starred_kol: false,
        follower_count: 41356,
      },
    ],
    follower_count_growth_rate_charts: [
      {
        ...unimportantAttrs,
        uuid: brandInfo6.id,
        name: brandInfo6.name,
        starred_kol: false,
        follower_count_growth_rate: 0.10308057069778442,
      },
      {
        ...unimportantAttrs,
        uuid: brandInfo8.id,
        name: brandInfo8.name,
        starred_kol: false,
        follower_count_growth_rate: 0.001973201287910342,
      },
      {
        ...unimportantAttrs,
        uuid: brandInfo7.id,
        name: brandInfo7.name,
        starred_kol: false,
        follower_count_growth_rate: -0.0072843763045966625,
      },
    ],
    avg_view_rate_charts: [
      {
        ...unimportantAttrs,
        uuid: brandInfo7.id,
        name: brandInfo7.name,
        starred_kol: false,
        avg_view_rate: 3.525430609224698,
      },
      {
        ...unimportantAttrs,
        uuid: brandInfo6.id,
        name: brandInfo6.name,
        starred_kol: false,
        avg_view_rate: 0.9582475310210014,
      },
      {
        ...unimportantAttrs,
        uuid: brandInfo8.id,
        name: brandInfo8.name,
        starred_kol: false,
        avg_view_rate: 0.3475661012184942,
      },
    ],
    avg_engagement_rate_charts: [
      {
        ...unimportantAttrs,
        uuid: brandInfo7.id,
        name: brandInfo7.name,
        starred_kol: false,
        avg_engagement_rate: 0.0473688283173172,
      },
      {
        ...unimportantAttrs,
        uuid: brandInfo6.id,
        name: brandInfo6.name,
        starred_kol: false,
        avg_engagement_rate: 0.0308800804665731,
      },
      {
        ...unimportantAttrs,
        uuid: brandInfo8.id,
        name: brandInfo8.name,
        starred_kol: false,
        avg_engagement_rate: 0.0284603400296681,
      },
    ],
  }
}

export const makeDemoCompetitiveBrandAnalysisKolListMetricCharts = (
  locale?: SupportedLanguages,
): CompetitiveBrandAnalysisKolListMetricChartsResponse => {
  const brandInfo6 = makeBrandInfo6(locale)
  const brandInfo7 = makeBrandInfo7(locale)
  const brandInfo8 = makeBrandInfo8(locale)

  return [
    {
      ...unimportantAttrs,
      uuid: brandInfo6.id,
      name: brandInfo6.name,
      starred_kol: false,
      metric_value: 174,
    },
    {
      ...unimportantAttrs,
      uuid: brandInfo7.id,
      name: brandInfo7.name,
      starred_kol: false,
      metric_value: 17,
    },
    {
      ...unimportantAttrs,
      uuid: brandInfo8.id,
      name: brandInfo8.name,
      starred_kol: false,
      metric_value: 129,
    },
  ]
}

type HashTagMaker = (locale?: SupportedLanguages) => Hashtag
type SharedHashtagProps = Pick<
  Hashtag,
  'count' | 'engagement_count' | 'hashtag_id' | 'hashtag_status'
>

export const makeHashtag1: HashTagMaker = (locale?: SupportedLanguages) => {
  const shared: SharedHashtagProps = {
    count: 26,
    engagement_count: 45883,
    hashtag_id: null,
    hashtag_status: null,
  }

  switch (locale) {
    case SupportedLanguages.enUS:
      return {
        ...shared,
        hashtag: 'New Release',
      }
    case SupportedLanguages.ja:
      return {
        ...shared,
        hashtag: '新作公開',
      }
    case SupportedLanguages.zhTW:
    case SupportedLanguages.ach:
    default:
      return {
        ...shared,
        hashtag: '新片上線',
      }
  }
}

export const makeHashtag2: HashTagMaker = (locale?: SupportedLanguages) => {
  const shared: SharedHashtagProps = {
    count: 20,
    engagement_count: 87908,
    hashtag_id: null,
    hashtag_status: null,
  }

  switch (locale) {
    case SupportedLanguages.enUS:
      return {
        ...shared,
        hashtag: 'Like',
      }
    case SupportedLanguages.ja:
      return {
        ...shared,
        hashtag: 'いいね',
      }
    case SupportedLanguages.zhTW:
    case SupportedLanguages.ach:
    default:
      return {
        ...shared,
        hashtag: '按讚',
      }
  }
}

export const makeHashtag3: HashTagMaker = (locale?: SupportedLanguages) => {
  const shared: SharedHashtagProps = {
    count: 17,
    engagement_count: 33034,
    hashtag_id: null,
    hashtag_status: null,
  }

  switch (locale) {
    case SupportedLanguages.enUS:
      return {
        ...shared,
        hashtag: 'Daily Sharing',
      }
    case SupportedLanguages.ja:
      return {
        ...shared,
        hashtag: '日常シェア',
      }
    case SupportedLanguages.zhTW:
    case SupportedLanguages.ach:
    default:
      return {
        ...shared,
        hashtag: '日常分享',
      }
  }
}

export const makeHashtag4: HashTagMaker = (locale?: SupportedLanguages) => {
  const shared: SharedHashtagProps = {
    count: 16,
    engagement_count: 21505,
    hashtag_id: null,
    hashtag_status: null,
  }

  switch (locale) {
    case SupportedLanguages.enUS:
      return {
        ...shared,
        hashtag: 'Giveaway at the End',
      }
    case SupportedLanguages.ja:
      return {
        ...shared,
        hashtag: '投稿末抽選',
      }
    case SupportedLanguages.zhTW:
    case SupportedLanguages.ach:
    default:
      return {
        ...shared,
        hashtag: '文末抽獎',
      }
  }
}

export const makeHashtag5: HashTagMaker = (locale?: SupportedLanguages) => {
  const shared: SharedHashtagProps = {
    count: 15,
    engagement_count: 65456,
    hashtag_id: null,
    hashtag_status: null,
  }

  switch (locale) {
    case SupportedLanguages.enUS:
      return {
        ...shared,
        hashtag: 'Quality Life',
      }
    case SupportedLanguages.ja:
      return {
        ...shared,
        hashtag: '上質な生活',
      }
    case SupportedLanguages.zhTW:
    case SupportedLanguages.ach:
    default:
      return {
        ...shared,
        hashtag: '質感生活',
      }
  }
}

export const makeHashtag6: HashTagMaker = (locale?: SupportedLanguages) => {
  const shared: SharedHashtagProps = {
    count: 14,
    engagement_count: 16424,
    hashtag_id: null,
    hashtag_status: null,
  }

  switch (locale) {
    case SupportedLanguages.enUS:
      return {
        ...shared,
        hashtag: 'Gourmet',
      }
    case SupportedLanguages.ja:
      return {
        ...shared,
        hashtag: 'グルメ',
      }
    case SupportedLanguages.zhTW:
    case SupportedLanguages.ach:
    default:
      return {
        ...shared,
        hashtag: '美食',
      }
  }
}

export const makeHashtag7: HashTagMaker = (locale?: SupportedLanguages) => {
  const shared: SharedHashtagProps = {
    count: 10,
    engagement_count: 34308,
    hashtag_id: null,
    hashtag_status: null,
  }

  switch (locale) {
    case SupportedLanguages.enUS:
      return {
        ...shared,
        hashtag: 'Delicious',
      }
    case SupportedLanguages.ja:
      return {
        ...shared,
        hashtag: '美味しい',
      }
    case SupportedLanguages.zhTW:
    case SupportedLanguages.ach:
    default:
      return {
        ...shared,
        hashtag: '美味',
      }
  }
}

export const makeHashtag8: HashTagMaker = (locale?: SupportedLanguages) => {
  const shared: SharedHashtagProps = {
    count: 10,
    engagement_count: 16327,
    hashtag_id: null,
    hashtag_status: null,
  }

  switch (locale) {
    case SupportedLanguages.enUS:
      return {
        ...shared,
        hashtag: 'Foodie',
      }
    case SupportedLanguages.ja:
      return {
        ...shared,
        hashtag: '食いしん坊',
      }
    case SupportedLanguages.zhTW:
    case SupportedLanguages.ach:
    default:
      return {
        ...shared,
        hashtag: '吃貨',
      }
  }
}

export const makeHashtag9: HashTagMaker = (locale?: SupportedLanguages) => {
  const shared: SharedHashtagProps = {
    count: 6,
    engagement_count: 6947,
    hashtag_id: null,
    hashtag_status: null,
  }

  switch (locale) {
    case SupportedLanguages.enUS:
      return {
        ...shared,
        hashtag: 'Food Sharing',
      }
    case SupportedLanguages.ja:
      return {
        ...shared,
        hashtag: 'グルメシェア',
      }
    case SupportedLanguages.zhTW:
    case SupportedLanguages.ach:
    default:
      return {
        ...shared,
        hashtag: '美食分享',
      }
  }
}

export const makeHashtag10: HashTagMaker = (locale?: SupportedLanguages) => {
  const shared: SharedHashtagProps = {
    count: 6,
    engagement_count: 6947,
    hashtag_id: null,
    hashtag_status: null,
  }

  switch (locale) {
    case SupportedLanguages.enUS:
      return {
        ...shared,
        hashtag: 'Food Diary',
      }
    case SupportedLanguages.ja:
      return {
        ...shared,
        hashtag: 'グルメ日記',
      }
    case SupportedLanguages.zhTW:
    case SupportedLanguages.ach:
    default:
      return {
        ...shared,
        hashtag: '美食日記',
      }
  }
}

export const makeHashtag11: HashTagMaker = (locale?: SupportedLanguages) => {
  const shared: SharedHashtagProps = {
    count: 6,
    engagement_count: 6947,
    hashtag_id: null,
    hashtag_status: null,
  }

  switch (locale) {
    case SupportedLanguages.enUS:
      return {
        ...shared,
        hashtag: 'Food Adventure',
      }
    case SupportedLanguages.ja:
      return {
        ...shared,
        hashtag: 'グルメ探検',
      }
    case SupportedLanguages.zhTW:
    case SupportedLanguages.ach:
    default:
      return {
        ...shared,
        hashtag: '美食探險',
      }
  }
}

export const makeHashtag12: HashTagMaker = (locale?: SupportedLanguages) => {
  const shared: SharedHashtagProps = {
    count: 5,
    engagement_count: 16219,
    hashtag_id: null,
    hashtag_status: null,
  }

  switch (locale) {
    case SupportedLanguages.enUS:
      return {
        ...shared,
        hashtag: 'Food Photography',
      }
    case SupportedLanguages.ja:
      return {
        ...shared,
        hashtag: 'グルメフォト',
      }
    case SupportedLanguages.zhTW:
    case SupportedLanguages.ach:
    default:
      return {
        ...shared,
        hashtag: '美食攝影',
      }
  }
}

export const makeHashtag13: HashTagMaker = (locale?: SupportedLanguages) => {
  const shared: SharedHashtagProps = {
    count: 5,
    engagement_count: 7961,
    hashtag_id: null,
    hashtag_status: null,
  }

  switch (locale) {
    case SupportedLanguages.enUS:
      return {
        ...shared,
        hashtag: 'Food Recommendation',
      }
    case SupportedLanguages.ja:
      return {
        ...shared,
        hashtag: 'グルメおすすめ',
      }
    case SupportedLanguages.zhTW:
    case SupportedLanguages.ach:
    default:
      return {
        ...shared,
        hashtag: '美食推薦',
      }
  }
}

export const makeHashtag14: HashTagMaker = (locale?: SupportedLanguages) => {
  const shared: SharedHashtagProps = {
    count: 4,
    engagement_count: 35378,
    hashtag_id: null,
    hashtag_status: null,
  }

  switch (locale) {
    case SupportedLanguages.enUS:
      return {
        ...shared,
        hashtag: 'Food Journey',
      }
    case SupportedLanguages.ja:
      return {
        ...shared,
        hashtag: 'グルメ旅',
      }
    case SupportedLanguages.zhTW:
    case SupportedLanguages.ach:
    default:
      return {
        ...shared,
        hashtag: '美食之旅',
      }
  }
}

export const makeHashtag15: HashTagMaker = (locale?: SupportedLanguages) => {
  const shared: SharedHashtagProps = {
    count: 4,
    engagement_count: 8947,
    hashtag_id: null,
    hashtag_status: null,
  }

  switch (locale) {
    case SupportedLanguages.enUS:
      return {
        ...shared,
        hashtag: 'Food Paradise',
      }
    case SupportedLanguages.ja:
      return {
        ...shared,
        hashtag: 'グルメ天国',
      }
    case SupportedLanguages.zhTW:
    case SupportedLanguages.ach:
    default:
      return {
        ...shared,
        hashtag: '美食天堂',
      }
  }
}

export const makeHashtag16: HashTagMaker = (locale?: SupportedLanguages) => {
  const shared: SharedHashtagProps = {
    count: 4,
    engagement_count: 6482,
    hashtag_id: null,
    hashtag_status: null,
  }

  switch (locale) {
    case SupportedLanguages.enUS:
      return {
        ...shared,
        hashtag: 'Foodie Daily Life',
      }
    case SupportedLanguages.ja:
      return {
        ...shared,
        hashtag: '食いしん坊日常',
      }
    case SupportedLanguages.zhTW:
    case SupportedLanguages.ach:
    default:
      return {
        ...shared,
        hashtag: '吃貨日常',
      }
  }
}

export const makeHashtag17: HashTagMaker = (locale?: SupportedLanguages) => {
  const shared: SharedHashtagProps = {
    count: 4,
    engagement_count: 6478,
    hashtag_id: null,
    hashtag_status: null,
  }

  switch (locale) {
    case SupportedLanguages.enUS:
      return {
        ...shared,
        hashtag: 'Happy Dining Table',
      }
    case SupportedLanguages.ja:
      return {
        ...shared,
        hashtag: '幸せな食卓',
      }
    case SupportedLanguages.zhTW:
    case SupportedLanguages.ach:
    default:
      return {
        ...shared,
        hashtag: '幸福餐桌',
      }
  }
}

export const makeHashtag18: HashTagMaker = (locale?: SupportedLanguages) => {
  const shared: SharedHashtagProps = {
    count: 4,
    engagement_count: 3580,
    hashtag_id: null,
    hashtag_status: null,
  }

  switch (locale) {
    case SupportedLanguages.enUS:
      return {
        ...shared,
        hashtag: 'Home Cooking',
      }
    case SupportedLanguages.ja:
      return {
        ...shared,
        hashtag: '家庭グルメ',
      }
    case SupportedLanguages.zhTW:
    case SupportedLanguages.ach:
    default:
      return {
        ...shared,
        hashtag: '家庭美食',
      }
  }
}

export const makeHashtag19: HashTagMaker = (locale?: SupportedLanguages) => {
  const shared: SharedHashtagProps = {
    count: 4,
    engagement_count: 3397,
    hashtag_id: null,
    hashtag_status: null,
  }

  switch (locale) {
    case SupportedLanguages.enUS:
      return {
        ...shared,
        hashtag: 'Food Blogger',
      }
    case SupportedLanguages.ja:
      return {
        ...shared,
        hashtag: 'グルメブロガー',
      }
    case SupportedLanguages.zhTW:
    case SupportedLanguages.ach:
    default:
      return {
        ...shared,
        hashtag: '美食博主',
      }
  }
}

export const makeHashtag20: HashTagMaker = (locale?: SupportedLanguages) => {
  const shared: SharedHashtagProps = {
    count: 4,
    engagement_count: 2414,
    hashtag_id: null,
    hashtag_status: null,
  }

  switch (locale) {
    case SupportedLanguages.enUS:
      return {
        ...shared,
        hashtag: 'Food Creation',
      }
    case SupportedLanguages.ja:
      return {
        ...shared,
        hashtag: 'グルメ制作',
      }
    case SupportedLanguages.zhTW:
    case SupportedLanguages.ach:
    default:
      return {
        ...shared,
        hashtag: '美食製作',
      }
  }
}

export const makeHashtag21: HashTagMaker = (locale?: SupportedLanguages) => {
  const shared: SharedHashtagProps = {
    count: 4,
    engagement_count: 2414,
    hashtag_id: null,
    hashtag_status: null,
  }

  switch (locale) {
    case SupportedLanguages.enUS:
      return {
        ...shared,
        hashtag: 'Kitchen Fun',
      }
    case SupportedLanguages.ja:
      return {
        ...shared,
        hashtag: 'キッチン楽しみ',
      }
    case SupportedLanguages.zhTW:
    case SupportedLanguages.ach:
    default:
      return {
        ...shared,
        hashtag: '廚房樂趣',
      }
  }
}

export const makeHashtag22: HashTagMaker = (locale?: SupportedLanguages) => {
  const shared: SharedHashtagProps = {
    count: 4,
    engagement_count: 2414,
    hashtag_id: null,
    hashtag_status: null,
  }

  switch (locale) {
    case SupportedLanguages.enUS:
      return {
        ...shared,
        hashtag: 'Healthy Food',
      }
    case SupportedLanguages.ja:
      return {
        ...shared,
        hashtag: '健康グルメ',
      }
    case SupportedLanguages.zhTW:
    case SupportedLanguages.ach:
    default:
      return {
        ...shared,
        hashtag: '健康美食',
      }
  }
}

export const makeHashtag23: HashTagMaker = (locale?: SupportedLanguages) => {
  const shared: SharedHashtagProps = {
    count: 3,
    engagement_count: 5167,
    hashtag_id: null,
    hashtag_status: null,
  }

  switch (locale) {
    case SupportedLanguages.enUS:
      return {
        ...shared,
        hashtag: 'Quick Recipe',
      }
    case SupportedLanguages.ja:
      return {
        ...shared,
        hashtag: '簡単レシピ',
      }
    case SupportedLanguages.zhTW:
    case SupportedLanguages.ach:
    default:
      return {
        ...shared,
        hashtag: '快速食譜',
      }
  }
}

export const makeHashtag24: HashTagMaker = (locale?: SupportedLanguages) => {
  const shared: SharedHashtagProps = {
    count: 3,
    engagement_count: 4964,
    hashtag_id: null,
    hashtag_status: null,
  }

  switch (locale) {
    case SupportedLanguages.enUS:
      return {
        ...shared,
        hashtag: 'Creative Cuisine',
      }
    case SupportedLanguages.ja:
      return {
        ...shared,
        hashtag: '創作料理',
      }
    case SupportedLanguages.zhTW:
    case SupportedLanguages.ach:
    default:
      return {
        ...shared,
        hashtag: '創意料理',
      }
  }
}

export const makeHashtag25: HashTagMaker = (locale?: SupportedLanguages) => {
  const shared: SharedHashtagProps = {
    count: 3,
    engagement_count: 4827,
    hashtag_id: null,
    hashtag_status: null,
  }

  switch (locale) {
    case SupportedLanguages.enUS:
      return {
        ...shared,
        hashtag: 'Food Art',
      }
    case SupportedLanguages.ja:
      return {
        ...shared,
        hashtag: 'グルメアート',
      }
    case SupportedLanguages.zhTW:
    case SupportedLanguages.ach:
    default:
      return {
        ...shared,
        hashtag: '美食藝術',
      }
  }
}

export const makeHashtag26: HashTagMaker = (locale?: SupportedLanguages) => {
  const shared: SharedHashtagProps = {
    count: 3,
    engagement_count: 4827,
    hashtag_id: null,
    hashtag_status: null,
  }

  switch (locale) {
    case SupportedLanguages.enUS:
      return {
        ...shared,
        hashtag: 'Delicious Moments',
      }
    case SupportedLanguages.ja:
      return {
        ...shared,
        hashtag: '美味しい時間',
      }
    case SupportedLanguages.zhTW:
    case SupportedLanguages.ach:
    default:
      return {
        ...shared,
        hashtag: '美味時光',
      }
  }
}

export const makeHashtag27: HashTagMaker = (locale?: SupportedLanguages) => {
  const shared: SharedHashtagProps = {
    count: 3,
    engagement_count: 4827,
    hashtag_id: null,
    hashtag_status: null,
  }

  switch (locale) {
    case SupportedLanguages.enUS:
      return {
        ...shared,
        hashtag: 'Cooking Class',
      }
    case SupportedLanguages.ja:
    case SupportedLanguages.zhTW:
    case SupportedLanguages.ach:
    default:
      return {
        ...shared,
        hashtag: '料理教室',
      }
  }
}

export const makeHashtag28: HashTagMaker = (locale?: SupportedLanguages) => {
  const shared: SharedHashtagProps = {
    count: 3,
    engagement_count: 4401,
    hashtag_id: null,
    hashtag_status: null,
  }

  switch (locale) {
    case SupportedLanguages.enUS:
      return {
        ...shared,
        hashtag: 'Vegetarian Cuisine',
      }
    case SupportedLanguages.ja:
      return {
        ...shared,
        hashtag: 'ベジタリアン料理',
      }
    case SupportedLanguages.zhTW:
    case SupportedLanguages.ach:
    default:
      return {
        ...shared,
        hashtag: '素食料理',
      }
  }
}

export const makeHashtag29: HashTagMaker = (locale?: SupportedLanguages) => {
  const shared: SharedHashtagProps = {
    count: 3,
    engagement_count: 4401,
    hashtag_id: null,
    hashtag_status: null,
  }

  switch (locale) {
    case SupportedLanguages.enUS:
      return {
        ...shared,
        hashtag: 'Homemade Food',
      }
    case SupportedLanguages.ja:
      return {
        ...shared,
        hashtag: '手作りグルメ',
      }
    case SupportedLanguages.zhTW:
    case SupportedLanguages.ach:
    default:
      return {
        ...shared,
        hashtag: '自製美食',
      }
  }
}

export const makeHashtag30: HashTagMaker = (locale?: SupportedLanguages) => {
  const shared: SharedHashtagProps = {
    count: 3,
    engagement_count: 4230,
    hashtag_id: null,
    hashtag_status: null,
  }

  switch (locale) {
    case SupportedLanguages.enUS:
      return {
        ...shared,
        hashtag: 'Dessert Lover',
      }
    case SupportedLanguages.ja:
      return {
        ...shared,
        hashtag: 'スイーツ好き',
      }
    case SupportedLanguages.zhTW:
    case SupportedLanguages.ach:
    default:
      return {
        ...shared,
        hashtag: '甜點控',
      }
  }
}

export const makeDemoStaticHashtags = (
  locale?: SupportedLanguages,
): Hashtag[] => [
  makeHashtag1(locale),
  makeHashtag2(locale),
  makeHashtag3(locale),
  makeHashtag4(locale),
  makeHashtag5(locale),
  makeHashtag6(locale),
  makeHashtag7(locale),
  makeHashtag8(locale),
  makeHashtag9(locale),
  makeHashtag10(locale),
  makeHashtag11(locale),
  makeHashtag12(locale),
  makeHashtag13(locale),
  makeHashtag14(locale),
  makeHashtag15(locale),
  makeHashtag16(locale),
  makeHashtag17(locale),
  makeHashtag18(locale),
  makeHashtag19(locale),
  makeHashtag20(locale),
  makeHashtag21(locale),
  makeHashtag22(locale),
  makeHashtag23(locale),
  makeHashtag24(locale),
  makeHashtag25(locale),
  makeHashtag26(locale),
  makeHashtag27(locale),
  makeHashtag28(locale),
  makeHashtag29(locale),
  makeHashtag30(locale),
]

export const makeDemoCompetitiveBrandKolStatisticListCharts = (
  locale?: SupportedLanguages,
): CompetitiveKolStatisticListCharts => {
  const brandInfo6 = makeBrandInfo6(locale)
  const brandInfo7 = makeBrandInfo7(locale)
  const brandInfo8 = makeBrandInfo8(locale)

  return {
    follower_percentage_charts: [
      {
        uuid: brandInfo6.id,
        name: brandInfo6.name,
        starred_kol: false,
        follower_percentages: [
          {
            platform: PlatformShortcode.YouTube,
            follower_percentage: 0.4854943753700414,
          },
          {
            platform: PlatformShortcode.Facebook,
            follower_percentage: 0.35126899546082496,
          },
          {
            platform: PlatformShortcode.Instagram,
            follower_percentage: 0.16323662916913362,
          },
        ],
      },
      {
        uuid: brandInfo7.id,
        name: brandInfo7.name,
        starred_kol: false,
        follower_percentages: [
          {
            platform: PlatformShortcode.Facebook,
            follower_percentage: 0.25669609002942334,
          },
          {
            platform: PlatformShortcode.Instagram,
            follower_percentage: 0.7433039099705766,
          },
        ],
      },
      {
        uuid: brandInfo8.id,
        name: brandInfo8.name,
        starred_kol: false,
        follower_percentages: [
          {
            platform: PlatformShortcode.Facebook,
            follower_percentage: 0.6877191306424648,
          },
          {
            platform: PlatformShortcode.Instagram,
            follower_percentage: 0.31228086935753524,
          },
        ],
      },
    ],
    engagement_count_charts: [
      {
        uuid: brandInfo6.id,
        name: brandInfo6.name,
        starred_kol: false,
        engagement_counts: [
          {
            platform: PlatformShortcode.YouTube,
            engagement_count: 127027,
          },
          {
            platform: PlatformShortcode.Facebook,
            engagement_count: 94169,
          },
          {
            platform: PlatformShortcode.Instagram,
            engagement_count: 43406,
          },
        ],
      },
      {
        uuid: brandInfo7.id,
        name: brandInfo7.name,
        starred_kol: false,
        engagement_counts: [
          {
            platform: PlatformShortcode.Facebook,
            engagement_count: 17057,
          },
          {
            platform: PlatformShortcode.Instagram,
            engagement_count: 49167,
          },
        ],
      },
      {
        uuid: brandInfo8.id,
        name: brandInfo8.name,
        starred_kol: false,
        engagement_counts: [
          {
            platform: PlatformShortcode.Facebook,
            engagement_count: 155307,
          },
          {
            platform: PlatformShortcode.Instagram,
            engagement_count: 69676,
          },
        ],
      },
    ],
  }
}

export const makeDemoCompetitiveBrandKolStatisticListTable = (
  locale?: SupportedLanguages,
): CompetitiveKolStatisticListResponse => {
  const brandInfo6 = makeBrandInfo6(locale)
  const brandInfo7 = makeBrandInfo7(locale)
  const brandInfo8 = makeBrandInfo8(locale)

  return {
    previous_page: null,
    next_page: null,
    total: 3,
    data: [
      {
        starred_kol: false,
        kol_name: brandInfo6.name,
        uuid: brandInfo6.id,
        platforms: [
          {
            platform: 1,
            link_id: 1268,
            platform_name: 'FB粉絲團',
            platform_id: '170637493009609',
            link_url: 'https://www.facebook.com/170637493009609',
          },
          {
            platform: 3,
            link_id: 1269,
            platform_name: 'IG',
            platform_id: 'lachahua.cat',
            link_url: 'https://www.instagram.com/lachahua.cat',
          },
          {
            platform: 4,
            link_id: 127805,
            platform_name: 'TikTok',
            platform_id: 'lachahua',
            link_url: 'https://www.tiktok.com/@lachahua',
          },
        ],
        follower: 384237,
        follower_count_growth_rate: -0.0072677948585454,
        avg_like_count: 1544.1349206349207,
        avg_comment_count: 46.567460317460316,
        avg_share_count: 15.468253968253968,
        avg_view_count: 19951.98275862069,
        view_rate: 0.05192624020753,
        avg_engagement_count: 1606.170634920635,
        engagement_rate: 0.0041801560883534,
        engagement_count: 404755,
        post_count: 252,
      },
      {
        starred_kol: false,
        kol_name: brandInfo7.name,
        uuid: brandInfo7.id,
        platforms: [
          {
            platform: 0,
            link_id: 169967,
            platform_name: 'YouTube',
            platform_id: 'UCt2eJPUFllX47b7RfTtPw2Q',
            link_url:
              'https://www.youtube.com/channel/UCt2eJPUFllX47b7RfTtPw2Q',
          },
          {
            platform: 1,
            link_id: 169968,
            platform_name: 'FB粉絲團',
            platform_id: '112524386896309',
            link_url: 'https://www.facebook.com/112524386896309',
          },
          {
            platform: 3,
            link_id: 169969,
            platform_name: 'IG',
            platform_id: 'uni_catto',
            link_url: 'https://www.instagram.com/uni_catto',
          },
        ],
        follower: 275163,
        follower_count_growth_rate: 0.0860982830076969,
        avg_like_count: 1474.9827160493828,
        avg_comment_count: 24.074074074074073,
        avg_share_count: 3.708641975308642,
        avg_view_count: 44654.38121546961,
        view_rate: 0.162283378272041,
        avg_engagement_count: 1502.7654320987654,
        engagement_rate: 0.0054613644715996,
        engagement_count: 608620,
        post_count: 405,
      },
      {
        starred_kol: false,
        kol_name: brandInfo8.name,
        uuid: brandInfo8.id,
        platforms: [
          {
            platform: 1,
            link_id: 16106,
            platform_name: 'FB粉絲團',
            platform_id: '2057098177835600',
            link_url: 'https://www.facebook.com/2057098177835600',
          },
          {
            platform: 3,
            link_id: 16107,
            platform_name: 'IG',
            platform_id: 'nsfw_cat_bobo',
            link_url: 'https://www.instagram.com/nsfw_cat_bobo',
          },
        ],
        follower: 65493,
        follower_count_growth_rate: -0.0066885066885067,
        avg_like_count: 1309.3529411764705,
        avg_comment_count: 7.5588235294117645,
        avg_share_count: 0.9705882352941176,
        avg_view_count: 98011,
        view_rate: 1.496511077519735,
        avg_engagement_count: 1317.8823529411766,
        engagement_rate: 0.0201224917615803,
        engagement_count: 44808,
        post_count: 34,
      },
    ],
  }
}

export const makeMentionedBrandA = (
  locale?: SupportedLanguages,
): CompetitiveAnalysisKol => {
  const brandInfo6 = makeBrandInfo6(locale)

  return {
    kol_uuid: brandInfo6.id,
    name: brandInfo6.name,
    starred_kol: false,
    follower_count: 663667,
    order_no: null,
    platform_type_list: [0, 1, 3],
  }
}

export const makeMentionedBrandB = (
  locale?: SupportedLanguages,
): CompetitiveAnalysisKol => {
  const brandInfo7 = makeBrandInfo7(locale)

  return {
    kol_uuid: brandInfo7.id,
    name: brandInfo7.name,
    starred_kol: false,
    follower_count: 812372,
    order_no: null,
    platform_type_list: [1, 3, 4],
  }
}

export const makeMentionedBrandC = (
  locale?: SupportedLanguages,
): CompetitiveAnalysisKol => {
  const brandInfo8 = makeBrandInfo8(locale)

  return {
    kol_uuid: brandInfo8.id,
    name: brandInfo8.name,
    starred_kol: false,
    follower_count: 954260,
    order_no: null,
    platform_type_list: [0, 1, 3, 4, 5],
  }
}

const TREND_DATA_DATE_LIST = [
  '2023-12-01',
  '2024-01-01',
  '2024-02-01',
  '2024-03-01',
  '2024-04-01',
  '2024-05-01',
] as const

export const makeDemoCompetitiveMentionedKolList = (
  locale?: SupportedLanguages,
): CompetitiveAnalysisKolList => {
  const mentionedBrandA = makeMentionedBrandA(locale)
  const mentionedBrandB = makeMentionedBrandB(locale)
  const mentionedBrandC = makeMentionedBrandC(locale)

  return {
    kols: [mentionedBrandA, mentionedBrandB, mentionedBrandC],
  }
}

export const makeDemoCompetitiveMentionEffectivenessTrendChartData = (
  locale?: SupportedLanguages,
): FetchCompetitiveMentionEffectivenessTrendChartDataResponse => {
  const mentionedBrandA = makeMentionedBrandA(locale)
  const mentionedBrandB = makeMentionedBrandB(locale)
  const mentionedBrandC = makeMentionedBrandC(locale)

  return {
    kol_infos: [
      {
        ...unimportantAttrs,
        uuid: mentionedBrandA.kol_uuid,
        name: mentionedBrandA.name,
        trend_data: [
          {
            key: TREND_DATA_DATE_LIST[0],
            from: '2023-12-01T00:00:00Z',
            to: '2023-12-31T23:59:59.999999999Z',
            social_volume_count: 15246,
            post_count: 3,
            kol_count: 3,
            avg_follower_count: 8084,
            like_count: 227,
            comment_count: 47,
            view_count: 14972,
            share_count: 0,
            engagement_count: 0,
          },
          {
            key: TREND_DATA_DATE_LIST[1],
            from: '2024-01-01T00:00:00Z',
            to: '2024-01-31T23:59:59.999999999Z',
            social_volume_count: 1380,
            post_count: 2,
            kol_count: 1,
            avg_follower_count: 7271,
            like_count: 1018,
            comment_count: 362,
            view_count: 0,
            share_count: 0,
            engagement_count: 0,
          },
          {
            key: TREND_DATA_DATE_LIST[2],
            from: '2024-02-01T00:00:00Z',
            to: '2024-02-29T23:59:59.999999999Z',
            social_volume_count: 27,
            post_count: 1,
            kol_count: 1,
            avg_follower_count: 862,
            like_count: 27,
            comment_count: 0,
            view_count: 0,
            share_count: 0,
            engagement_count: 0,
          },
          {
            key: TREND_DATA_DATE_LIST[3],
            from: '2024-03-01T00:00:00Z',
            to: '2024-03-31T23:59:59.999999999Z',
            social_volume_count: 120764,
            post_count: 6,
            kol_count: 3,
            avg_follower_count: 12330,
            like_count: 2218,
            comment_count: 66,
            view_count: 118480,
            share_count: 0,
            engagement_count: 0,
          },
          {
            key: TREND_DATA_DATE_LIST[4],
            from: '2024-04-01T00:00:00Z',
            to: '2024-04-30T23:59:59.999999999Z',
            social_volume_count: 22,
            post_count: 2,
            kol_count: 2,
            avg_follower_count: 11712,
            like_count: 21,
            comment_count: 1,
            view_count: 0,
            share_count: 0,
            engagement_count: 0,
          },
          {
            key: TREND_DATA_DATE_LIST[5],
            from: '2024-05-01T00:00:00Z',
            to: '2024-05-31T23:59:59.999999999Z',
            social_volume_count: 31,
            post_count: 1,
            kol_count: 1,
            avg_follower_count: 3051,
            like_count: 31,
            comment_count: 0,
            view_count: 0,
            share_count: 0,
            engagement_count: 0,
          },
        ],
      },
      {
        ...unimportantAttrs,
        uuid: mentionedBrandB.kol_uuid,
        name: mentionedBrandB.name,
        trend_data: [
          {
            key: TREND_DATA_DATE_LIST[0],
            from: '2023-12-01T00:00:00Z',
            to: '2023-12-31T23:59:59.999999999Z',
            social_volume_count: 18908,
            post_count: 4,
            kol_count: 4,
            avg_follower_count: 26484,
            like_count: 2440,
            comment_count: 53,
            view_count: 16415,
            share_count: 0,
            engagement_count: 0,
          },
          {
            key: TREND_DATA_DATE_LIST[1],
            from: '2024-01-01T00:00:00Z',
            to: '2024-01-31T23:59:59.999999999Z',
            social_volume_count: 13828,
            post_count: 2,
            kol_count: 2,
            avg_follower_count: 51889,
            like_count: 443,
            comment_count: 19,
            view_count: 13366,
            share_count: 0,
            engagement_count: 0,
          },
          {
            key: TREND_DATA_DATE_LIST[2],
            from: '2024-02-01T00:00:00Z',
            to: '2024-02-29T23:59:59.999999999Z',
            social_volume_count: 106372,
            post_count: 5,
            kol_count: 3,
            avg_follower_count: 42036,
            like_count: 2365,
            comment_count: 325,
            view_count: 103682,
            share_count: 0,
            engagement_count: 0,
          },
          {
            key: TREND_DATA_DATE_LIST[3],
            from: '2024-03-01T00:00:00Z',
            to: '2024-03-31T23:59:59.999999999Z',
            social_volume_count: 0,
            post_count: 0,
            kol_count: 0,
            avg_follower_count: 0,
            like_count: 0,
            comment_count: 0,
            view_count: 0,
            share_count: 0,
            engagement_count: 0,
          },
          {
            key: TREND_DATA_DATE_LIST[4],
            from: '2024-04-01T00:00:00Z',
            to: '2024-04-30T23:59:59.999999999Z',
            social_volume_count: 2364,
            post_count: 1,
            kol_count: 1,
            avg_follower_count: 104620,
            like_count: 2327,
            comment_count: 37,
            view_count: 0,
            share_count: 0,
            engagement_count: 0,
          },
          {
            key: TREND_DATA_DATE_LIST[5],
            from: '2024-05-01T00:00:00Z',
            to: '2024-05-31T23:59:59.999999999Z',
            social_volume_count: 0,
            post_count: 0,
            kol_count: 0,
            avg_follower_count: 0,
            like_count: 0,
            comment_count: 0,
            view_count: 0,
            share_count: 0,
            engagement_count: 0,
          },
        ],
      },
      {
        ...unimportantAttrs,
        uuid: mentionedBrandC.kol_uuid,
        name: mentionedBrandC.name,
        trend_data: [
          {
            key: TREND_DATA_DATE_LIST[0],
            from: '2023-12-01T00:00:00Z',
            to: '2023-12-31T23:59:59.999999999Z',
            social_volume_count: 0,
            post_count: 0,
            kol_count: 0,
            avg_follower_count: 0,
            like_count: 0,
            comment_count: 0,
            view_count: 0,
            share_count: 0,
            engagement_count: 0,
          },
          {
            key: TREND_DATA_DATE_LIST[1],
            from: '2024-01-01T00:00:00Z',
            to: '2024-01-31T23:59:59.999999999Z',
            social_volume_count: 0,
            post_count: 0,
            kol_count: 0,
            avg_follower_count: 0,
            like_count: 0,
            comment_count: 0,
            view_count: 0,
            share_count: 0,
            engagement_count: 0,
          },
          {
            key: TREND_DATA_DATE_LIST[2],
            from: '2024-02-01T00:00:00Z',
            to: '2024-02-29T23:59:59.999999999Z',
            social_volume_count: 0,
            post_count: 0,
            kol_count: 0,
            avg_follower_count: 0,
            like_count: 0,
            comment_count: 0,
            view_count: 0,
            share_count: 0,
            engagement_count: 0,
          },
          {
            key: TREND_DATA_DATE_LIST[3],
            from: '2024-03-01T00:00:00Z',
            to: '2024-03-31T23:59:59.999999999Z',
            social_volume_count: 8226,
            post_count: 2,
            kol_count: 2,
            avg_follower_count: 398816,
            like_count: 8170,
            comment_count: 56,
            view_count: 0,
            share_count: 0,
            engagement_count: 0,
          },
          {
            key: TREND_DATA_DATE_LIST[4],
            from: '2024-04-01T00:00:00Z',
            to: '2024-04-30T23:59:59.999999999Z',
            social_volume_count: 0,
            post_count: 0,
            kol_count: 0,
            avg_follower_count: 0,
            like_count: 0,
            comment_count: 0,
            view_count: 0,
            share_count: 0,
            engagement_count: 0,
          },
          {
            key: TREND_DATA_DATE_LIST[5],
            from: '2024-05-01T00:00:00Z',
            to: '2024-05-31T23:59:59.999999999Z',
            social_volume_count: 0,
            post_count: 0,
            kol_count: 0,
            avg_follower_count: 0,
            like_count: 0,
            comment_count: 0,
            view_count: 0,
            share_count: 0,
            engagement_count: 0,
          },
        ],
      },
    ],
  }
}

type TrendingPost = Pick<
  CompetitiveMentionEffectivenessTrendChartDetail,
  | 'kol_uuid'
  | 'post_author_name'
  | 'post_id'
  | 'post_title'
  | 'post_engagement_count'
  | 'post_thumbnail_url'
>

type SharedTrendingPostProps = Pick<
  TrendingPost,
  | 'kol_uuid'
  | 'post_id'
  | 'post_engagement_count'
  | 'post_thumbnail_url'
  | 'post_title'
>

type TrendingPostMaker = (locale?: SupportedLanguages) => TrendingPost

export const makeTrendingPost1: TrendingPostMaker = (locale) => {
  const shared: SharedTrendingPostProps = {
    kol_uuid: `${MOCK_KOL_INFO_PREFIX}-1`,
    post_id: 'post-1',
    post_engagement_count: 180,
    post_thumbnail_url: demoPostSrc1,
    post_title: makePostContent1(locale),
  }

  switch (locale) {
    case SupportedLanguages.enUS:
    case SupportedLanguages.ja:
    case SupportedLanguages.zhTW:
    case SupportedLanguages.ach:
    default:
      return {
        ...shared,
        post_author_name: 'Foodieeee Food',
      }
  }
}

export const makeTrendingPost2: TrendingPostMaker = (locale) => {
  const shared: SharedTrendingPostProps = {
    kol_uuid: `${MOCK_KOL_INFO_PREFIX}-2`,
    post_id: 'post-2',
    post_engagement_count: 1609,
    post_thumbnail_url: demoPostSrc2,
    post_title: makePostContent2(locale),
  }

  switch (locale) {
    case SupportedLanguages.enUS:
      return {
        ...shared,
        post_author_name: 'Jolice Foodie',
      }
    case SupportedLanguages.ja:
      return {
        ...shared,
        post_author_name: 'カフェアドベンチャー',
      }
    case SupportedLanguages.zhTW:
    case SupportedLanguages.ach:
    default:
      return {
        ...shared,
        post_author_name: '美食探險家',
      }
  }
}

export const makeTrendingPost3: TrendingPostMaker = (locale) => {
  const shared: SharedTrendingPostProps = {
    kol_uuid: `${MOCK_KOL_INFO_PREFIX}-3`,
    post_id: 'post-3',
    post_engagement_count: 27,
    post_thumbnail_url: demoPostSrc3,
    post_title: makePostContent3(locale),
  }

  switch (locale) {
    case SupportedLanguages.enUS:
      return {
        ...shared,
        post_author_name: 'WoW Food',
      }
    case SupportedLanguages.ja:
      return {
        ...shared,
        post_author_name: 'もぐもぐ日記',
      }
    case SupportedLanguages.zhTW:
    case SupportedLanguages.ach:
    default:
      return {
        ...shared,
        post_author_name: '王小明吃美食 Xiao Min Foodie',
      }
  }
}

export const makeTrendingPost4: TrendingPostMaker = (locale) => {
  const shared: SharedTrendingPostProps = {
    kol_uuid: `${MOCK_KOL_INFO_PREFIX}-4`,
    post_id: 'post-4',
    post_engagement_count: 1141,
    post_thumbnail_url: demoPostSrc4,
    post_title: makePostContent4(locale),
  }

  switch (locale) {
    case SupportedLanguages.enUS:
      return {
        ...shared,
        post_author_name: 'Fresh day with food',
      }
    case SupportedLanguages.ja:
      return {
        ...shared,
        post_author_name: 'フードライフ',
      }
    case SupportedLanguages.zhTW:
    case SupportedLanguages.ach:
    default:
      return {
        ...shared,
        post_author_name: '好好料理',
      }
  }
}

export const makeTrendingPost5: TrendingPostMaker = (locale) => {
  const shared: SharedTrendingPostProps = {
    kol_uuid: `${MOCK_KOL_INFO_PREFIX}-5`,
    post_id: 'post-5',
    post_engagement_count: 21,
    post_thumbnail_url: demoPostSrc5,
    post_title: makePostContent5(locale),
  }

  switch (locale) {
    case SupportedLanguages.enUS:
      return {
        ...shared,
        post_author_name: 'Delish Adventures',
      }
    case SupportedLanguages.ja:
      return {
        ...shared,
        post_author_name: 'うまいもんハンター',
      }
    case SupportedLanguages.zhTW:
    case SupportedLanguages.ach:
    default:
      return {
        ...shared,
        post_author_name: '廚房奇兵',
      }
  }
}

export const makeTrendingPost6: TrendingPostMaker = (locale) => {
  const shared: SharedTrendingPostProps = {
    kol_uuid: `${MOCK_KOL_INFO_PREFIX}-6`,
    post_id: 'post-6',
    post_engagement_count: 31,
    post_thumbnail_url: demoPostSrc6,
    post_title: makePostContent6(locale),
  }

  switch (locale) {
    case SupportedLanguages.enUS:
      return {
        ...shared,
        post_author_name: 'Joy eat and drink',
      }
    case SupportedLanguages.ja:
      return {
        ...shared,
        post_author_name: 'Joyカフェ巡りログ ',
      }
    case SupportedLanguages.zhTW:
    case SupportedLanguages.ach:
    default:
      return {
        ...shared,
        post_author_name: 'Joy 喬伊吃吃喝喝',
      }
  }
}

export const makeTrendingPost7: TrendingPostMaker = (locale) => {
  const shared: SharedTrendingPostProps = {
    kol_uuid: `${MOCK_KOL_INFO_PREFIX}-7`,
    post_id: 'post-7',
    post_engagement_count: 1698,
    post_thumbnail_url: demoPostSrc7,
    post_title: makePostContent7(locale),
  }

  switch (locale) {
    case SupportedLanguages.enUS:
      return {
        ...shared,
        post_author_name: 'Feast With Me',
      }
    case SupportedLanguages.ja:
      return {
        ...shared,
        post_author_name: 'おいしい発見',
      }
    case SupportedLanguages.zhTW:
    case SupportedLanguages.ach:
    default:
      return {
        ...shared,
        post_author_name: '星星去走走',
      }
  }
}

export const makeTrendingPost8: TrendingPostMaker = (locale) => {
  const shared: SharedTrendingPostProps = {
    kol_uuid: `${MOCK_KOL_INFO_PREFIX}-8`,
    post_id: 'post-8',
    post_engagement_count: 444,
    post_thumbnail_url: demoPostSrc8,
    post_title: makePostContent8(locale),
  }

  switch (locale) {
    case SupportedLanguages.enUS:
      return {
        ...shared,
        post_author_name: 'Delicous Traveler',
      }
    case SupportedLanguages.ja:
      return {
        ...shared,
        post_author_name: 'タベルノスキ',
      }
    case SupportedLanguages.zhTW:
    case SupportedLanguages.ach:
    default:
      return {
        ...shared,
        post_author_name: '味蕾旅行家',
      }
  }
}

export const makeTrendingPost9: TrendingPostMaker = (locale) => {
  const shared: SharedTrendingPostProps = {
    kol_uuid: `${MOCK_KOL_INFO_PREFIX}-9`,
    post_id: 'post-9',
    post_engagement_count: 1608,
    post_thumbnail_url: demoPostSrc9,
    post_title: makePostContent9(locale),
  }

  switch (locale) {
    case SupportedLanguages.enUS:
      return {
        ...shared,
        post_author_name: 'Flower🌸',
      }
    case SupportedLanguages.ja:
      return {
        ...shared,
        post_author_name: 'Flower',
      }
    case SupportedLanguages.zhTW:
    case SupportedLanguages.ach:
    default:
      return {
        ...shared,
        post_author_name: '花兒 Flower🌸',
      }
  }
}

export const makeTrendingPost10: TrendingPostMaker = (locale) => {
  const shared: SharedTrendingPostProps = {
    kol_uuid: `${MOCK_KOL_INFO_PREFIX}-10`,
    post_id: 'post-10',
    post_engagement_count: 2364,
    post_thumbnail_url: demoPostSrc10,
    post_title: makePostContent10(locale),
  }

  switch (locale) {
    case SupportedLanguages.enUS:
      return {
        ...shared,
        post_author_name: 'Bda travel together',
      }
    case SupportedLanguages.ja:
      return {
        ...shared,
        post_author_name: 'Bdaフードライフ',
      }
    case SupportedLanguages.zhTW:
    case SupportedLanguages.ach:
    default:
      return {
        ...shared,
        post_author_name: 'Bda 帶你吃吃喝喝',
      }
  }
}

export const makeTrendingPost11: TrendingPostMaker = (locale) => {
  const shared: SharedTrendingPostProps = {
    kol_uuid: `${MOCK_KOL_INFO_PREFIX}-1`,
    post_id: 'post-11',
    post_engagement_count: 8222,
    post_thumbnail_url: demoPostSrc1,
    post_title: makePostContent11(locale),
  }

  switch (locale) {
    case SupportedLanguages.enUS:
      return {
        ...shared,
        post_author_name: 'Tasty Bites Daily',
      }
    case SupportedLanguages.ja:
      return {
        ...shared,
        post_author_name: '食の冒険者',
      }
    case SupportedLanguages.zhTW:
    case SupportedLanguages.ach:
    default:
      return {
        ...shared,
        post_author_name: '芽米ㄉ頻道',
      }
  }
}

export const makeEmptyTrendingPost = (id: number): TrendingPost => {
  return {
    kol_uuid: `author-${id}`,
    post_author_name: '',
    post_id: `empty-post-${id}`,
    post_title: '',
    post_engagement_count: 0,
    post_thumbnail_url: '',
  }
}

export const makeTrendDetailDataDateList = (
  locale?: SupportedLanguages,
): Record<
  (typeof TREND_DATA_DATE_LIST)[number],
  Partial<CompetitiveMentionEffectivenessTrendChartDetail>
>[] => {
  return [
    {
      [TREND_DATA_DATE_LIST[0]]: makeTrendingPost1(locale),
      [TREND_DATA_DATE_LIST[1]]: makeTrendingPost2(locale),
      [TREND_DATA_DATE_LIST[2]]: makeTrendingPost3(locale),
      [TREND_DATA_DATE_LIST[3]]: makeTrendingPost4(locale),
      [TREND_DATA_DATE_LIST[4]]: makeTrendingPost5(locale),
      [TREND_DATA_DATE_LIST[5]]: makeTrendingPost6(locale),
    },
    {
      [TREND_DATA_DATE_LIST[0]]: makeTrendingPost7(locale),
      [TREND_DATA_DATE_LIST[1]]: makeTrendingPost8(locale),
      [TREND_DATA_DATE_LIST[2]]: makeTrendingPost9(locale),
      [TREND_DATA_DATE_LIST[3]]: makeEmptyTrendingPost(1),
      [TREND_DATA_DATE_LIST[4]]: makeTrendingPost10(locale),
      [TREND_DATA_DATE_LIST[5]]: makeEmptyTrendingPost(2),
    },
    {
      [TREND_DATA_DATE_LIST[0]]: makeEmptyTrendingPost(3),
      [TREND_DATA_DATE_LIST[1]]: makeEmptyTrendingPost(4),
      [TREND_DATA_DATE_LIST[2]]: makeEmptyTrendingPost(5),
      [TREND_DATA_DATE_LIST[3]]: makeTrendingPost11(locale),
      [TREND_DATA_DATE_LIST[4]]: makeEmptyTrendingPost(6),
      [TREND_DATA_DATE_LIST[5]]: makeEmptyTrendingPost(7),
    },
  ]
}

const getDemoCompetitiveMentionedEfficientTrendDetail = (
  kolUuid: string,
  trendDataDate: string,
  locale?: SupportedLanguages,
): CompetitiveMentionEffectivenessTrendChartDetail => {
  const mentionedBrandA = makeMentionedBrandA(locale)
  const mentionedBrandB = makeMentionedBrandB(locale)
  const mentionedBrandC = makeMentionedBrandC(locale)
  const trendDetailDataDateList = makeTrendDetailDataDateList(locale)

  const postInfoRecord = {
    [mentionedBrandA.kol_uuid]: trendDetailDataDateList[0],
    [mentionedBrandB.kol_uuid]: trendDetailDataDateList[1],
    [mentionedBrandC.kol_uuid]: trendDetailDataDateList[2],
  }

  return {
    kol_uuid: '',
    kol_name: '',
    follower_count: 0,
    post_author_uuid: '',
    post_url: '',
    post_crawler_id: '',
    post_type: '',
    post_like_count: 0,
    post_comment_count: 0,
    post_view_count: 0,
    post_share_count: 0,
    post_social_volume_count: 1680,
    ...postInfoRecord[kolUuid]?.[trendDataDate],
  }
}

export const makeDemoCompetitiveMentionedKolFollowerChartData = (
  locale?: SupportedLanguages,
): FetchCompetitiveMentionKolFollowerChartDataResponse => {
  const mentionedBrandA = makeMentionedBrandA(locale)
  const mentionedBrandB = makeMentionedBrandB(locale)
  const mentionedBrandC = makeMentionedBrandC(locale)

  return {
    follower_percentage_charts: [
      {
        ...unimportantAttrs,
        uuid: mentionedBrandA.kol_uuid,
        name: mentionedBrandA.name,
        starred_kol: false,
        follower_level_percentages: [
          {
            follower_level: 'level1',
            percentage: 0.1111111111111111,
          },
          {
            follower_level: 'level2',
            percentage: 0.5555555555555556,
          },
          {
            follower_level: 'level3',
            percentage: 0.3333333333333333,
          },
          {
            follower_level: 'level4',
            percentage: 0,
          },
          {
            follower_level: 'level5',
            percentage: 0,
          },
        ],
      },
      {
        ...unimportantAttrs,
        uuid: mentionedBrandB.kol_uuid,
        name: mentionedBrandB.name,
        starred_kol: false,
        follower_level_percentages: [
          {
            follower_level: 'level1',
            percentage: 0.6363636363636364,
          },
          {
            follower_level: 'level2',
            percentage: 0.36363636363636365,
          },
          {
            follower_level: 'level3',
            percentage: 0,
          },
          {
            follower_level: 'level4',
            percentage: 0,
          },
          {
            follower_level: 'level5',
            percentage: 0,
          },
        ],
      },
      {
        ...unimportantAttrs,
        uuid: mentionedBrandC.kol_uuid,
        name: mentionedBrandC.name,
        starred_kol: false,
        follower_level_percentages: [
          {
            follower_level: 'level1',
            percentage: 0,
          },
          {
            follower_level: 'level2',
            percentage: 0.5,
          },
          {
            follower_level: 'level3',
            percentage: 0,
          },
          {
            follower_level: 'level4',
            percentage: 0.5,
          },
          {
            follower_level: 'level5',
            percentage: 0,
          },
        ],
      },
    ],
  }
}

const DEMO_COMPETITIVE_MENTION_KOL_PROPERTY_CHART_DATA: FetchCompetitiveMentionKolPropertyChartDataResponse =
  {
    mention_kol_property_charts: [
      {
        customized_tags_name: 'Food',
        competitive_kol_percentage: 0.7083333333333334,
        starred_kol_percentage: null,
      },
      {
        customized_tags_name: 'Daily Topics',
        competitive_kol_percentage: 0.3333333333333333,
        starred_kol_percentage: null,
      },
      {
        customized_tags_name: 'Education',
        competitive_kol_percentage: 0.2916666666666667,
        starred_kol_percentage: null,
      },
      {
        customized_tags_name: 'Food/Beverage',
        competitive_kol_percentage: 0.25,
        starred_kol_percentage: null,
      },
      {
        customized_tags_name: 'Daily Topics/Holidays',
        competitive_kol_percentage: 0.20833333333333334,
        starred_kol_percentage: null,
      },
      {
        customized_tags_name: 'Food/Dessert',
        competitive_kol_percentage: 0.20833333333333334,
        starred_kol_percentage: null,
      },
      {
        customized_tags_name: 'Art and Entertainment',
        competitive_kol_percentage: 0.16666666666666666,
        starred_kol_percentage: null,
      },
      {
        customized_tags_name: 'Travel',
        competitive_kol_percentage: 0.16666666666666666,
        starred_kol_percentage: null,
      },
      {
        customized_tags_name: 'Fortunetelling',
        competitive_kol_percentage: 0.08333333333333333,
        starred_kol_percentage: null,
      },
      {
        customized_tags_name: 'Travel/Taiwan Travel',
        competitive_kol_percentage: 0.08333333333333333,
        starred_kol_percentage: null,
      },
    ],
  }

export const makeDemoCompetitiveMentionSimilarKolList = (
  locale?: SupportedLanguages,
): FetchCompetitiveMentionSimilarKolListResponse => {
  const post1 = makeTrendingPost1(locale)
  const post2 = makeTrendingPost2(locale)
  const post3 = makeTrendingPost3(locale)
  const post4 = makeTrendingPost4(locale)
  const post5 = makeTrendingPost5(locale)
  const post6 = makeTrendingPost6(locale)
  const post7 = makeTrendingPost7(locale)
  const post8 = makeTrendingPost8(locale)
  const post9 = makeTrendingPost9(locale)
  const post10 = makeTrendingPost10(locale)

  return {
    competitive_mention_similar_kols: [
      {
        type: KolIdentityType.Person,
        kol_uuid: post1.kol_uuid,
        name: post1.post_author_name,
        follower_count: 311531,
        customized_tags_names: [
          'Art and Entertainment',
          'Daily Topics',
          'Family',
          'Sports',
        ],
      },
      {
        type: KolIdentityType.Person,
        kol_uuid: post2.kol_uuid,
        name: post2.post_author_name,
        follower_count: 175992,
        customized_tags_names: [
          'Beauty and Fashion',
          'Daily Topics',
          'Education',
          'Travel',
          'Daily Topics/Celebrity News',
        ],
      },
      {
        type: KolIdentityType.Person,
        kol_uuid: post3.kol_uuid,
        name: post3.post_author_name,
        follower_count: 143557,
        customized_tags_names: [
          'Gaming/Live Streaming',
          'Beauty and Fashion',
          'Daily Topics',
          'Food',
          'Beauty and Fashion/Outfit',
        ],
      },
      {
        type: KolIdentityType.Person,
        kol_uuid: post4.kol_uuid,
        name: post4.post_author_name,
        follower_count: 140932,
        customized_tags_names: [
          'Daily Topics',
          'Education',
          'Family',
          'Medical and Health',
          'Travel',
        ],
      },
      {
        type: KolIdentityType.Person,
        kol_uuid: post5.kol_uuid,
        name: post5.post_author_name,
        follower_count: 106034,
        customized_tags_names: [
          'Sports/Basketball',
          'Media Entertainment/K-pop',
          'Sports/Yoga',
          'Daily Topics',
          'Education',
          'Sports',
        ],
      },
      {
        type: KolIdentityType.Person,
        kol_uuid: post6.kol_uuid,
        name: post6.post_author_name,
        follower_count: 104013,
        customized_tags_names: [
          'Art and Entertainment',
          'Daily Topics',
          'Education',
        ],
      },
      {
        type: KolIdentityType.Person,
        kol_uuid: post7.kol_uuid,
        name: post7.post_author_name,
        follower_count: 98017,
        customized_tags_names: [
          'Relationship',
          'Relationship/Wedding',
          'Education',
          'Family',
          'Medical and Health',
        ],
      },
      {
        type: KolIdentityType.Person,
        kol_uuid: post8.kol_uuid,
        name: post8.post_author_name,
        follower_count: 96700,
        customized_tags_names: [
          'Relationship',
          'Beauty and Fashion',
          'Daily Topics',
          'Fortunetelling',
          'Photography',
        ],
      },
      {
        type: KolIdentityType.Person,
        kol_uuid: post9.kol_uuid,
        name: post9.post_author_name,
        follower_count: 87181,
        customized_tags_names: [
          'Beauty and Fashion',
          'Daily Topics',
          'Beauty and Fashion/Outfit',
        ],
      },
      {
        type: KolIdentityType.Person,
        kol_uuid: post10.kol_uuid,
        name: post10.post_author_name,
        follower_count: 85466,
        customized_tags_names: [
          'Gaming/Mobile Game',
          'Daily Topics',
          'Family',
          'Pets',
        ],
      },
    ],
  }
}

const MENTIONED_TOP_TEN_REST_NON_DISPLAY_PROPS = {
  url: '',
  crawler_id: '',
  platform_type: PlatformShortcode.Instagram,
  is_advertorial: false,
  is_post_collected: false,
  social_volume_count: 8222,
  like_count: 8170,
  comment_count: 52,
  share_count: 0,
  view_count: 0,
  play_count: 0,
}

export const makeDemoCompetitiveMentionedTopTenPostList = (
  locale?: SupportedLanguages,
): CompetitiveMentionTopTenPostList => {
  const mentionedBrandA = makeMentionedBrandA(locale)
  const mentionedBrandB = makeMentionedBrandB(locale)
  const mentionedBrandC = makeMentionedBrandC(locale)

  const post1 = makeTrendingPost1(locale)
  const post2 = makeTrendingPost2(locale)
  const post3 = makeTrendingPost3(locale)
  const post4 = makeTrendingPost4(locale)
  const post5 = makeTrendingPost5(locale)
  const post6 = makeTrendingPost6(locale)
  const post7 = makeTrendingPost7(locale)
  const post8 = makeTrendingPost8(locale)
  const post9 = makeTrendingPost9(locale)
  const post10 = makeTrendingPost10(locale)

  return [
    {
      type: 'image',
      post_id: post1.post_id,
      ig_post_id: 'post-3320743675063514329',
      thumbnail_url: post1.post_thumbnail_url,
      title: '',
      content: post1.post_title,
      is_disable_like: false,
      published_at: '2024-03-10T15:11:56Z',
      mention_kols: [
        {
          uuid: mentionedBrandC.kol_uuid,
          name: mentionedBrandC.name,
          follower_count: 954260,
        },
      ],
      kol_uuid: post1.kol_uuid,
      kol_name: post1.post_author_name,
      kol_potential: Potential.Normal,
      kol_customized_tags_names: [
        'Art and Entertainment',
        'Beauty and Fashion',
        'Daily Topics',
        'Daily Topics/Holidays',
      ],
      engagement_count: 8222,
      ...MENTIONED_TOP_TEN_REST_NON_DISPLAY_PROPS,
    },
    {
      type: 'image',
      post_id: post1.post_id,
      ig_post_id: 'post-3347541375368140622',
      thumbnail_url: post2.post_thumbnail_url,
      title: '',
      content: post2.post_title,
      published_at: '2024-04-16T14:34:10Z',
      is_disable_like: false,
      mention_kols: [
        {
          uuid: mentionedBrandB.kol_uuid,
          name: mentionedBrandB.name,
          follower_count: 663667,
        },
      ],
      engagement_count: 2364,
      kol_uuid: post2.kol_uuid,
      kol_name: post2.post_author_name,
      kol_potential: Potential.Normal,
      kol_customized_tags_names: ['Education', 'Food', 'Food/Beverage'],
      ...MENTIONED_TOP_TEN_REST_NON_DISPLAY_PROPS,
    },
    {
      type: 'image',
      post_id: post1.post_id,
      ig_post_id: 'post-3261282668922154523',
      is_disable_like: false,
      thumbnail_url: post3.post_thumbnail_url,
      title: '',
      content: post3.post_title,
      published_at: '2023-12-19T14:13:31Z',
      mention_kols: [
        {
          uuid: mentionedBrandB.kol_uuid,
          name: mentionedBrandB.name,
          follower_count: 663667,
        },
      ],
      kol_uuid: post3.kol_uuid,
      kol_name: post3.post_author_name,
      kol_potential: Potential.Normal,
      kol_customized_tags_names: [
        'Art and Entertainment/Podcast',
        'Food/Dessert',
        'Daily Topics',
        'Education',
        'Family',
        'Food',
        'Pets',
        'Beauty and Fashion/Outfit',
      ],
      engagement_count: 1698,
      ...MENTIONED_TOP_TEN_REST_NON_DISPLAY_PROPS,
    },
    {
      type: 'short',
      post_id: post1.post_id,
      ig_post_id: 'post-3308235289645672636',
      is_disable_like: false,
      thumbnail_url: post4.post_thumbnail_url,
      title: '',
      content: post4.post_title,
      published_at: '2024-02-22T09:00:22Z',
      mention_kols: [
        {
          uuid: mentionedBrandB.kol_uuid,
          name: mentionedBrandB.name,
          follower_count: 663667,
        },
      ],
      kol_uuid: post4.kol_uuid,
      kol_name: post4.post_author_name,
      kol_potential: Potential.Normal,
      kol_customized_tags_names: [
        'Art and Entertainment',
        'Food',
        'Medical and Health',
        'Travel',
        'Daily Topics/Holidays',
        'Food/Beverage',
        'Travel/Taiwan Travel',
      ],
      engagement_count: 1613,
      ...MENTIONED_TOP_TEN_REST_NON_DISPLAY_PROPS,
    },
    {
      type: 'short',
      post_id: post1.post_id,
      ig_post_id: 'post-3331264066558391261',
      is_disable_like: false,
      thumbnail_url: post5.post_thumbnail_url,
      title: '',
      content: post5.post_title,
      published_at: '2024-03-25T03:36:49Z',
      mention_kols: [
        {
          uuid: mentionedBrandA.kol_uuid,
          name: mentionedBrandA.name,
          follower_count: 812372,
        },
      ],
      kol_uuid: post5.kol_uuid,
      kol_name: post5.post_author_name,
      kol_potential: Potential.Normal,
      kol_customized_tags_names: ['Food', 'Fortunetelling', 'Law and Society'],
      engagement_count: 1141,
      ...MENTIONED_TOP_TEN_REST_NON_DISPLAY_PROPS,
    },
    {
      type: 'short',
      post_id: post1.post_id,
      ig_post_id: 'post-3331264066558391261',
      is_disable_like: false,
      thumbnail_url: post6.post_thumbnail_url,
      title: '',
      content: post6.post_title,
      published_at: '2024-03-25T03:36:49Z',
      mention_kols: [
        {
          uuid: mentionedBrandA.kol_uuid,
          name: mentionedBrandA.name,
          follower_count: 812372,
        },
      ],
      kol_uuid: post6.kol_uuid,
      kol_name: post6.post_author_name,
      kol_potential: Potential.Normal,
      kol_customized_tags_names: ['Food', 'Fortunetelling', 'Law and Society'],
      engagement_count: 690,
      ...MENTIONED_TOP_TEN_REST_NON_DISPLAY_PROPS,
    },
    {
      type: 'image',
      post_id: post1.post_id,
      ig_post_id: 'post-3275054260542041196',
      is_disable_like: false,
      thumbnail_url: post7.post_thumbnail_url,
      title: '',
      content: post7.post_title,
      published_at: '2024-01-07T14:15:13Z',
      mention_kols: [
        {
          uuid: mentionedBrandB.kol_uuid,
          name: mentionedBrandB.name,
          follower_count: 812372,
        },
      ],
      kol_uuid: post7.kol_uuid,
      kol_name: post7.post_author_name,
      kol_potential: Potential.Normal,
      kol_customized_tags_names: ['Education', 'Food'],
      engagement_count: 493,
      ...MENTIONED_TOP_TEN_REST_NON_DISPLAY_PROPS,
    },
    {
      type: 'image',
      post_id: post1.post_id,
      ig_post_id: 'post-3275054260542041196',
      is_disable_like: false,
      thumbnail_url: post8.post_thumbnail_url,
      title: '',
      content: post8.post_title,
      published_at: '2024-01-07T14:15:13Z',
      mention_kols: [
        {
          uuid: mentionedBrandA.kol_uuid,
          name: mentionedBrandA.name,
          follower_count: 812372,
        },
      ],
      kol_uuid: post8.kol_uuid,
      kol_name: post8.post_author_name,
      kol_potential: Potential.Normal,
      kol_customized_tags_names: ['Education', 'Food'],
      engagement_count: 455,
      ...MENTIONED_TOP_TEN_REST_NON_DISPLAY_PROPS,
    },
    {
      type: 'image',
      post_id: post1.post_id,
      ig_post_id: 'post-3304726597952505891',
      is_disable_like: false,
      thumbnail_url: post9.post_thumbnail_url,
      title: '',
      content: post9.post_title,
      published_at: '2024-02-17T12:48:51Z',
      mention_kols: [
        {
          uuid: mentionedBrandB.kol_uuid,
          name: mentionedBrandB.name,
          follower_count: 663667,
        },
      ],
      kol_uuid: post9.kol_uuid,
      kol_name: post9.post_author_name,
      kol_potential: Potential.Normal,
      kol_customized_tags_names: ['Food/Dessert', 'Food', 'Food/Beverage'],
      engagement_count: 444,
      ...MENTIONED_TOP_TEN_REST_NON_DISPLAY_PROPS,
    },
    {
      type: 'image',
      post_id: post1.post_id,
      ig_post_id: 'post-3304726597952505891',
      is_disable_like: false,
      thumbnail_url: post10.post_thumbnail_url,
      title: '',
      content: post10.post_title,
      mention_kols: [
        {
          uuid: mentionedBrandB.kol_uuid,
          name: mentionedBrandB.name,
          follower_count: 663667,
        },
      ],
      kol_uuid: post10.kol_uuid,
      kol_name: post10.post_author_name,
      kol_potential: Potential.Normal,
      kol_customized_tags_names: ['Food/Dessert', 'Food', 'Food/Beverage'],
      engagement_count: 226,
      published_at: '2024-02-17T12:48:51Z',
      ...MENTIONED_TOP_TEN_REST_NON_DISPLAY_PROPS,
    },
  ]
}

export const makeDemoCompetitiveMentionPostKolList = (
  locale?: SupportedLanguages,
): FetchCompetitiveMentionPostKolListResponse => {
  const mentionedBrandA = makeMentionedBrandA(locale)
  const mentionedBrandB = makeMentionedBrandB(locale)
  const mentionedBrandC = makeMentionedBrandC(locale)
  const post1 = makeTrendingPost1(locale)
  const post2 = makeTrendingPost2(locale)
  const post3 = makeTrendingPost3(locale)
  const post4 = makeTrendingPost4(locale)
  const post5 = makeTrendingPost5(locale)
  const post6 = makeTrendingPost6(locale)
  const post7 = makeTrendingPost7(locale)
  const post8 = makeTrendingPost8(locale)
  const post9 = makeTrendingPost9(locale)
  const post10 = makeTrendingPost10(locale)

  return {
    data: [
      {
        kol_name: post1.post_author_name,
        potential: Potential.Normal,
        customized_tags_names: [
          'Art and Entertainment',
          'Beauty and Fashion',
          'Daily Topics',
          'Daily Topics/Holidays',
        ],
        uuid: post1.kol_uuid,
        platform: {
          platform: 3,
        },
        is_verified: false,
        is_radar_star: false,
        is_collection_kol: false,
        follower_count: 776110,
        follower_count_growth_rate: 0.013836383,
        avg_like_count: 8170,
        avg_comment_count: 52,
        avg_share_count: 0,
        avg_view_count: 0,
        avg_view_rate: 0,
        avg_engagement_count: 8222,
        avg_engagement_rate: 0.010593859478831291,
        post_count: 2,
        posts: [
          {
            type: 'image',
            is_disable_like: false,
            post_id: post1.post_id,
            ig_post_id: 'post-',
            url: 'https://www.instagram.com/p/C4VpsdGSTTZ',
            thumbnail_url: post1.post_thumbnail_url,
            title: '',
            content: post1.post_title,
            crawler_id: '299152231',
            platform_type: PlatformShortcode.Instagram,
            published_at: '2024-03-10T15:11:56Z',
            mention_kols: [
              {
                uuid: mentionedBrandC.kol_uuid,
                name: mentionedBrandC.name,
                follower_count: 954260,
              },
            ],
            is_advertorial: true,
            like_count: 8170,
            comment_count: 52,
            share_count: 0,
            view_count: 0,
            play_count: 0,
            is_post_collected: false,
          },
        ],
      },
      {
        kol_name: post2.post_author_name,
        potential: Potential.Normal,
        customized_tags_names: ['Education', 'Food', 'Food/Beverage'],
        uuid: post2.kol_uuid,
        platform: {
          platform: 3,
        },
        is_verified: false,
        is_radar_star: false,
        is_collection_kol: false,
        follower_count: 104620,
        follower_count_growth_rate: 0.05282225,
        avg_like_count: 2327,
        avg_comment_count: 37,
        avg_share_count: 0,
        avg_view_count: 0,
        avg_view_rate: 0,
        avg_engagement_count: 2364,
        avg_engagement_rate: 0.02259606122970581,
        post_count: 1,
        posts: [
          {
            type: 'image',
            post_id: post2.post_id,
            is_disable_like: false,
            ig_post_id: 'post-',
            url: 'https://www.instagram.com/p/C502yUVvZdO',
            thumbnail_url: post2.post_thumbnail_url,
            title: '',
            content: post2.post_title,
            crawler_id: '11941118848',
            platform_type: PlatformShortcode.Instagram,
            published_at: '2024-04-16T14:34:10Z',
            mention_kols: [
              {
                uuid: mentionedBrandC.kol_uuid,
                name: mentionedBrandC.name,
                follower_count: 663667,
              },
            ],
            is_advertorial: false,
            like_count: 2327,
            comment_count: 37,
            share_count: 0,
            view_count: 0,
            play_count: 0,
            is_post_collected: false,
          },
        ],
      },
      {
        kol_name: post3.post_author_name,
        potential: Potential.Normal,
        customized_tags_names: [
          'Art and Entertainment',
          'Food',
          'Medical and Health',
          'Travel',
          'Daily Topics/Holidays',
          'Food/Beverage',
          'Travel/Taiwan Travel',
        ],
        uuid: post3.kol_uuid,
        platform: {
          platform: 3,
        },
        is_verified: false,
        is_radar_star: false,
        is_collection_kol: false,
        follower_count: 85891,
        follower_count_growth_rate: 0.44449303,
        avg_like_count: 1596,
        avg_comment_count: 17,
        avg_share_count: 0,
        avg_view_count: 78907,
        avg_view_rate: 0.918687641620636,
        avg_engagement_count: 1613,
        avg_engagement_rate: 0.018779616802930832,
        post_count: 1,
        posts: [
          {
            type: 'short',
            post_id: post3.post_id,
            ig_post_id: 'post-',
            url: 'https://www.instagram.com/p/C3pNnfYvei8',
            thumbnail_url: post3.post_thumbnail_url,
            is_disable_like: false,
            title: '',
            content: post3.post_title,
            crawler_id: '363118600',
            platform_type: PlatformShortcode.Instagram,
            published_at: '2024-02-22T09:00:22Z',
            mention_kols: [
              {
                uuid: mentionedBrandB.kol_uuid,
                name: mentionedBrandB.name,
                follower_count: 663667,
              },
            ],
            is_advertorial: false,
            like_count: 1596,
            comment_count: 17,
            share_count: 0,
            view_count: 78907,
            play_count: 78907,
            is_post_collected: false,
          },
        ],
      },
      {
        kol_name: post4.post_author_name,
        potential: Potential.Normal,
        customized_tags_names: [
          'Pets/Cat',
          'Daily Topics',
          'Food',
          'Fortunetelling',
          'Daily Topics/Holidays',
        ],
        uuid: post4.kol_uuid,
        platform: {
          platform: 3,
        },
        is_verified: false,
        is_radar_star: false,
        is_collection_kol: false,
        follower_count: 57021,
        follower_count_growth_rate: 0.007651799,
        avg_like_count: 440,
        avg_comment_count: 4,
        avg_share_count: 0,
        avg_view_count: 0,
        avg_view_rate: 0,
        avg_engagement_count: 444,
        avg_engagement_rate: 0.007786605041474104,
        post_count: 2,
        posts: [
          {
            type: 'image',
            post_id: post4.post_id,
            is_disable_like: false,
            ig_post_id: 'post-',
            url: 'https://www.instagram.com/p/C1_zyL0SnOu',
            thumbnail_url: post4.post_thumbnail_url,
            title: '',
            content: post4.post_title,
            crawler_id: '2221063875',
            platform_type: PlatformShortcode.Instagram,
            published_at: '2024-01-12T10:34:01Z',
            mention_kols: [
              {
                uuid: mentionedBrandB.kol_uuid,
                name: mentionedBrandB.name,
                follower_count: 663667,
              },
            ],
            is_advertorial: true,
            like_count: 440,
            comment_count: 4,
            share_count: 0,
            view_count: 0,
            play_count: 0,
            is_post_collected: false,
          },
        ],
      },
      {
        kol_name: post5.post_author_name,
        potential: Potential.Normal,
        customized_tags_names: [
          'Food/Dessert',
          'Art and Entertainment',
          'Daily Topics',
          'Food',
          'Beauty and Fashion/Outfit',
        ],
        uuid: post5.kol_uuid,
        platform: {
          platform: 3,
        },
        is_verified: true,
        is_radar_star: false,
        is_collection_kol: false,
        follower_count: 46758,
        follower_count_growth_rate: 0.033029184,
        avg_like_count: 3,
        avg_comment_count: 30,
        avg_share_count: 0,
        avg_view_count: 13005,
        avg_view_rate: 0.27812352776527405,
        avg_engagement_count: 33,
        avg_engagement_rate: 0.0007057615875964984,
        post_count: 4,
        posts: [
          {
            type: 'short',
            post_id: post5.post_id,
            ig_post_id: 'post-',
            url: 'https://www.instagram.com/p/C3ksKKpSIFr',
            is_disable_like: false,
            thumbnail_url: post5.post_thumbnail_url,
            title: '',
            content: post5.post_title,
            crawler_id: '7569709581',
            platform_type: PlatformShortcode.Instagram,
            published_at: '2024-02-20T14:55:35Z',
            mention_kols: [
              {
                uuid: mentionedBrandA.kol_uuid,
                name: mentionedBrandA.name,
                follower_count: 663667,
              },
            ],
            is_advertorial: false,
            like_count: 3,
            comment_count: 45,
            share_count: 0,
            view_count: 12643,
            play_count: 12643,
            is_post_collected: false,
          },
        ],
      },
      {
        kol_name: post6.post_author_name,
        potential: Potential.Normal,
        customized_tags_names: ['Food', 'Food/Beverage'],
        uuid: post6.kol_uuid,
        platform: {
          platform: 3,
        },
        is_verified: true,
        is_radar_star: false,
        is_collection_kol: false,
        follower_count: 43146,
        follower_count_growth_rate: 0.01932527,
        avg_like_count: 225,
        avg_comment_count: 1,
        avg_share_count: 0,
        avg_view_count: 0,
        avg_view_rate: 0,
        avg_engagement_count: 226,
        avg_engagement_rate: 0.005238029174506664,
        post_count: 1,
        posts: [
          {
            type: 'image',
            post_id: post6.post_id,
            is_disable_like: false,
            ig_post_id: 'post-',
            url: 'https://www.instagram.com/p/C0dQf0_vVnD',
            thumbnail_url: post6.post_thumbnail_url,
            title: '',
            content: post6.post_title,
            crawler_id: '1936745263',
            platform_type: PlatformShortcode.Instagram,
            published_at: '2023-12-05T04:00:06Z',
            mention_kols: [
              {
                uuid: mentionedBrandA.kol_uuid,
                name: mentionedBrandA.name,
                follower_count: 663667,
              },
            ],
            is_advertorial: true,
            like_count: 225,
            comment_count: 1,
            share_count: 0,
            view_count: 0,
            play_count: 0,
            is_post_collected: false,
          },
        ],
      },
      {
        kol_name: post7.post_author_name,
        potential: Potential.Normal,
        customized_tags_names: [
          'Art and Entertainment/Podcast',
          'Food/Dessert',
          'Daily Topics',
          'Education',
          'Family',
          'Food',
          'Pets',
          'Beauty and Fashion/Outfit',
        ],
        uuid: post7.kol_uuid,
        platform: {
          platform: 3,
        },
        is_verified: true,
        is_radar_star: false,
        is_collection_kol: false,
        follower_count: 40423,
        follower_count_growth_rate: 0.13382138,
        avg_like_count: 1677,
        avg_comment_count: 21,
        avg_share_count: 0,
        avg_view_count: 0,
        avg_view_rate: 0,
        avg_engagement_count: 1698,
        avg_engagement_rate: 0.042005788534879684,
        post_count: 1,
        posts: [
          {
            type: 'image',
            post_id: post7.post_id,
            is_disable_like: false,
            ig_post_id: 'post-',
            url: 'https://www.instagram.com/p/C1CZ07xPi4b',
            thumbnail_url: post7.post_thumbnail_url,
            title: '',
            content: post7.post_title,
            crawler_id: '647214484',
            platform_type: PlatformShortcode.Instagram,
            published_at: '2023-12-19T14:13:31Z',
            mention_kols: [
              {
                uuid: mentionedBrandB.kol_uuid,
                name: mentionedBrandB.name,
                follower_count: 663667,
              },
            ],
            is_advertorial: false,
            like_count: 1677,
            comment_count: 21,
            share_count: 0,
            view_count: 0,
            play_count: 0,
            is_post_collected: false,
          },
        ],
      },
      {
        kol_name: post8.post_author_name,
        potential: Potential.Normal,
        customized_tags_names: ['Food', 'Fortunetelling', 'Law and Society'],
        uuid: post8.kol_uuid,
        platform: {
          platform: 3,
        },
        is_verified: true,
        is_radar_star: false,
        is_collection_kol: false,
        follower_count: 24572,
        follower_count_growth_rate: 0.7572767,
        avg_like_count: 1109,
        avg_comment_count: 32,
        avg_share_count: 0,
        avg_view_count: 59240,
        avg_view_rate: 2.410874128341675,
        avg_engagement_count: 1141,
        avg_engagement_rate: 0.04643496498465538,
        post_count: 2,
        posts: [
          {
            type: 'short',
            is_disable_like: false,
            post_id: post8.post_id,
            ig_post_id: 'post-',
            url: 'https://www.instagram.com/p/C47BwTMPHPd',
            thumbnail_url: post8.post_thumbnail_url,
            title: '',
            content: post8.post_title,
            crawler_id: '30362457642',
            platform_type: PlatformShortcode.Instagram,
            published_at: '2024-03-25T03:36:49Z',
            mention_kols: [
              {
                uuid: mentionedBrandB.kol_uuid,
                name: mentionedBrandB.name,
                follower_count: 812372,
              },
            ],
            is_advertorial: false,
            like_count: 1109,
            comment_count: 32,
            share_count: 0,
            view_count: 59240,
            play_count: 59240,
            is_post_collected: false,
          },
        ],
      },
      {
        kol_name: post9.post_author_name,
        potential: Potential.Normal,
        customized_tags_names: ['Daily Topics', 'Daily Topics/Holidays'],
        uuid: post9.kol_uuid,
        platform: {
          platform: 3,
        },
        is_verified: false,
        is_radar_star: false,
        is_collection_kol: false,
        follower_count: 21522,
        follower_count_growth_rate: 0.009001407,
        avg_like_count: 0,
        avg_comment_count: 4,
        avg_share_count: 0,
        avg_view_count: 0,
        avg_view_rate: 0,
        avg_engagement_count: 4,
        avg_engagement_rate: 0.00018585633370094,
        post_count: 2,
        posts: [
          {
            type: 'image',
            post_id: post9.post_id,
            is_disable_like: false,
            ig_post_id: 'post-',
            url: 'https://www.instagram.com/p/C4petTkybWm',
            thumbnail_url: post9.post_thumbnail_url,
            title: '',
            content: post9.post_title,
            crawler_id: '4043546999',
            platform_type: PlatformShortcode.Instagram,
            published_at: '2024-03-18T08:00:44Z',
            mention_kols: [
              {
                uuid: mentionedBrandB.kol_uuid,
                name: mentionedBrandB.name,
                follower_count: 954260,
              },
            ],
            is_advertorial: false,
            like_count: 0,
            comment_count: 4,
            share_count: 0,
            view_count: 0,
            play_count: 0,
            is_post_collected: false,
          },
        ],
      },
      {
        kol_name: post10.post_author_name,
        potential: null,
        customized_tags_names: ['Food', 'Food/Recipe'],
        uuid: post10.kol_uuid,
        platform: {
          platform: 3,
        },
        is_verified: false,
        is_radar_star: false,
        is_collection_kol: false,
        follower_count: 21367,
        follower_count_growth_rate: 1.0237734,
        avg_like_count: 428,
        avg_comment_count: 27,
        avg_share_count: 0,
        avg_view_count: 16415,
        avg_view_rate: 0.768240749835968,
        avg_engagement_count: 455,
        avg_engagement_rate: 0.021294519305229187,
        post_count: 1,
        posts: [
          {
            type: 'short',
            post_id: post10.post_id,
            is_disable_like: false,
            ig_post_id: 'post-',
            url: 'https://www.instagram.com/p/C1VnoLjqZSL',
            thumbnail_url: post10.post_thumbnail_url,
            title: '',
            content: post10.post_title,
            crawler_id: '18660580309',
            platform_type: PlatformShortcode.Instagram,
            published_at: '2023-12-27T01:20:20Z',
            mention_kols: [
              {
                uuid: mentionedBrandB.kol_uuid,
                name: mentionedBrandB.name,
                follower_count: 663667,
              },
            ],
            is_advertorial: true,
            like_count: 428,
            comment_count: 27,
            share_count: 0,
            view_count: 16415,
            play_count: 16415,
            is_post_collected: false,
          },
        ],
      },
    ],
    paging: {
      cursors: {
        after:
          'eyJzb3J0Ijp7ImZpZWxkIjoiZm9sbG93ZXJfY291bnRfc3RhdHMubWF4Iiwib3JkZXIiOiJkZXNjZW5kaW5nIn0sIm5leHQiOlsiMTM0NzkxIiwiNDcxMjYiLCIyNjI1MiIsIjE4ODE0IiwiMTA4NTMzIiwiMTI4MzAiLCI5ODAzNSIsIjczMDk3IiwiODk2NjYiLCIzMDI5OTI3Il19',
        before: null,
      },
    },
  }
}

export const makeDemoCompetitiveMentionAnalysisKolAnalyzeList = (
  locale?: SupportedLanguages,
): CompetitiveMentionAnalysisKolAnalyzeListResponse => {
  const mentionedBrandA = makeMentionedBrandA(locale)
  const mentionedBrandB = makeMentionedBrandB(locale)
  const mentionedBrandC = makeMentionedBrandC(locale)

  return {
    page: 1,
    per_page: 3,
    total: 3,
    total_page: 1,
    data: [
      {
        kol_info: {
          ...unimportantAttrs,
          uuid: mentionedBrandB.kol_uuid,
          name: mentionedBrandB.name,
          starred: false,
        },
        mention: {
          kol_count: 9,
          post_count: 10,
          social_volume_count: 128804,
          like_count: 7193,
          comment_count: 280,
          share_count: 0,
          engagement_count: 7473,
          view_count: 121331,
        },
      },
      {
        kol_info: {
          ...unimportantAttrs,
          uuid: mentionedBrandC.kol_uuid,
          name: mentionedBrandC.name,
          starred: false,
        },
        mention: {
          kol_count: 2,
          post_count: 2,
          social_volume_count: 8226,
          like_count: 8170,
          comment_count: 56,
          share_count: 0,
          engagement_count: 8226,
          view_count: 0,
        },
      },
      {
        kol_info: {
          ...unimportantAttrs,
          uuid: mentionedBrandA.kol_uuid,
          name: mentionedBrandA.name,
          starred: false,
        },
        mention: {
          kol_count: 11,
          post_count: 11,
          social_volume_count: 76398,
          like_count: 1924,
          comment_count: 262,
          share_count: 0,
          engagement_count: 2186,
          view_count: 74212,
        },
      },
    ],
  }
}

export const DEMO_COMPETITIVE_KEYWORD_ANALYSIS_ID = -1

const DEMO_COMPETITIVE_KEYWORD_ANALYSIS: CompetitiveKeywordAnalysis = {
  id: DEMO_COMPETITIVE_KEYWORD_ANALYSIS_ID,
  name: 'DEMO',
  competitive_keyword_count: 3,
  updated_at: dayjs().format(),
  creator: {
    id: 0,
    name: 'Kolr',
  },
}

type KeywordMaker = (
  locale?: SupportedLanguages,
) => CompetitiveKeywordAnalysisKeyword
type SharedKeywordProps = Pick<
  CompetitiveKeywordAnalysisKeyword,
  'id' | 'starred_keyword' | 'order_no'
>

export const makeKeywordA: KeywordMaker = (locale) => {
  const shared: SharedKeywordProps = {
    id: 1,
    starred_keyword: false,
    order_no: 1,
  }

  switch (locale) {
    case SupportedLanguages.enUS:
      return {
        ...shared,
        name: 'Keyword Ａ',
        content: 'Food OR Chief',
      }
    case SupportedLanguages.ja:
      return {
        ...shared,
        name: 'キーワードA',
        content: 'フード OR シェフ',
      }
    case SupportedLanguages.zhTW:
    case SupportedLanguages.ach:
    default:
      return {
        ...shared,
        name: '關鍵字 Ａ',
        content: '食材 OR 主廚',
      }
  }
}

export const makeKeywordB: KeywordMaker = (locale) => {
  const shared: SharedKeywordProps = {
    id: 2,
    starred_keyword: false,
    order_no: 1,
  }

  switch (locale) {
    case SupportedLanguages.enUS:
      return {
        ...shared,
        name: 'Keyword B',
        content: 'Dessert',
      }
    case SupportedLanguages.ja:
      return {
        ...shared,
        name: 'キーワードB',
        content: 'デザート',
      }
    case SupportedLanguages.zhTW:
    case SupportedLanguages.ach:
    default:
      return {
        ...shared,
        name: '關鍵字 B',
        content: '甜點',
      }
  }
}

export const makeKeywordC: KeywordMaker = (locale) => {
  const shared: SharedKeywordProps = {
    id: 3,
    starred_keyword: false,
    order_no: 1,
  }

  switch (locale) {
    case SupportedLanguages.enUS:
      return {
        ...shared,
        name: 'Keyword C',
        content: 'Cafe AND Morning',
      }
    case SupportedLanguages.ja:
      return {
        ...shared,
        name: 'キーワードC',
        content: 'カフェ AND モーニング',
      }
    case SupportedLanguages.zhTW:
    case SupportedLanguages.ach:
    default:
      return {
        ...shared,
        name: '關鍵字 C',
        content: '咖啡 AND 早晨',
      }
  }
}

export const makeDemoCompetitiveKeywordAnalysisKeywordList = (
  locale?: SupportedLanguages,
): CompetitiveKeywordAnalysisKeywordList => {
  const keywordA = makeKeywordA(locale)
  const keywordB = makeKeywordB(locale)
  const keywordC = makeKeywordC(locale)

  return {
    keywords: [keywordA, keywordB, keywordC],
  }
}

export const makeDemoCompetitiveKeywordKolFollowerChartData = (
  locale?: SupportedLanguages,
): FetchCompetitiveKeywordKolFollowerChartDataResponse => {
  const keywordA = makeKeywordA(locale)
  const keywordB = makeKeywordB(locale)
  const keywordC = makeKeywordC(locale)
  const chartData = makeDemoCompetitiveMentionedKolFollowerChartData(locale)

  return {
    follower_percentage_charts: [
      {
        ...pick(keywordA, ['id', 'name', 'starred_keyword']),
        follower_level_percentages:
          chartData.follower_percentage_charts[0].follower_level_percentages,
      },
      {
        ...pick(keywordB, ['id', 'name', 'starred_keyword']),
        starred_keyword: false,
        follower_level_percentages:
          chartData.follower_percentage_charts[1].follower_level_percentages,
      },
      {
        ...pick(keywordC, ['id', 'name', 'starred_keyword']),
        starred_keyword: false,
        follower_level_percentages:
          chartData.follower_percentage_charts[2].follower_level_percentages,
      },
    ],
  }
}

export const makeDemoCompetitiveKeywordEffectivenessTrendChartData = (
  locale?: SupportedLanguages,
): CompetitiveKeywordEffectivenessTrendList => {
  const keywordA = makeKeywordA(locale)
  const keywordB = makeKeywordB(locale)
  const keywordC = makeKeywordC(locale)
  const chartData =
    makeDemoCompetitiveMentionEffectivenessTrendChartData(locale)

  return {
    keyword_infos: [
      {
        ...pick(keywordA, ['id', 'name']),
        trend_data: chartData.kol_infos[0].trend_data,
      },
      {
        ...pick(keywordB, ['id', 'name']),
        trend_data: chartData.kol_infos[1].trend_data,
      },
      {
        ...pick(keywordC, ['id', 'name']),
        trend_data: chartData.kol_infos[2].trend_data,
      },
    ],
  }
}

export const makeDemoCompetitiveKeywordTopTenPostList = (
  locale?: SupportedLanguages,
): CompetitiveKeywordTopTenPostList => {
  const keywordA = makeKeywordA(locale)
  const keywordB = makeKeywordB(locale)
  const keywordC = makeKeywordC(locale)
  const postList = makeDemoCompetitiveMentionedTopTenPostList(locale)

  return [
    {
      ...postList[0],
      mention_keywords: [keywordC],
    },
    {
      ...postList[1],
      mention_keywords: [keywordB],
    },
    {
      ...postList[2],
      mention_keywords: [keywordB],
    },
    {
      ...postList[3],
      mention_keywords: [keywordB],
    },
    {
      ...postList[4],
      mention_keywords: [keywordA],
    },
    {
      ...postList[5],
      mention_keywords: [keywordA],
    },
    {
      ...postList[6],
      mention_keywords: [keywordB],
    },
    {
      ...postList[7],
      mention_keywords: [keywordB],
    },
    {
      ...postList[8],
      mention_keywords: [keywordB],
    },
    {
      ...postList[9],
      mention_keywords: [keywordB],
    },
  ]
}

export const makeDemoCompetitiveKeywordPostKolList = (
  locale?: SupportedLanguages,
): FetchCompetitiveKeywordPostKolListResponse => {
  const keywordA = makeKeywordA(locale)
  const keywordB = makeKeywordB(locale)
  const keywordC = makeKeywordC(locale)
  const kolList = makeDemoCompetitiveMentionPostKolList(locale)

  return {
    data: [
      {
        ...omit(kolList.data[0], 'post'),
        posts: [
          {
            ...omit(kolList.data[0].posts[0], 'mention_kols'),
            mention_keywords: [keywordC],
          },
        ],
      },
      {
        ...omit(kolList.data[1], 'post'),
        posts: [
          {
            ...omit(kolList.data[1].posts[0], 'mention_kols'),
            mention_keywords: [keywordB],
          },
        ],
      },
      {
        ...omit(kolList.data[2], 'post'),
        posts: [
          {
            ...omit(kolList.data[2].posts[0], 'mention_kols'),
            mention_keywords: [keywordB],
          },
        ],
      },
      {
        ...omit(kolList.data[3], 'post'),
        posts: [
          {
            ...omit(kolList.data[3].posts[0], 'mention_kols'),
            mention_keywords: [keywordB],
          },
        ],
      },
      {
        ...omit(kolList.data[4], 'post'),
        posts: [
          {
            ...omit(kolList.data[4].posts[0], 'mention_kols'),
            mention_keywords: [keywordB],
          },
        ],
      },
      {
        ...omit(kolList.data[5], 'post'),
        posts: [
          {
            ...omit(kolList.data[5].posts[0], 'mention_kols'),
            mention_keywords: [keywordB],
          },
        ],
      },
      {
        ...omit(kolList.data[6], 'post'),
        posts: [
          {
            ...omit(kolList.data[6].posts[0], 'mention_kols'),
            mention_keywords: [keywordB],
          },
        ],
      },
      {
        ...omit(kolList.data[7], 'post'),
        posts: [
          {
            ...omit(kolList.data[7].posts[0], 'mention_kols'),
            mention_keywords: [keywordA],
          },
        ],
      },
      {
        ...omit(kolList.data[8], 'post'),
        posts: [
          {
            ...omit(kolList.data[8].posts[0], 'mention_kols'),
            mention_keywords: [keywordC],
          },
        ],
      },
      {
        ...omit(kolList.data[9], 'post'),
        posts: [
          {
            ...omit(kolList.data[9].posts[0], 'mention_kols'),
            mention_keywords: [keywordB],
          },
        ],
      },
    ],
    paging: kolList.paging,
  }
}

export const makeDemoCompetitiveKeywordStatisticList = (
  locale?: SupportedLanguages,
): CompetitiveKeywordAnalysisStatisticList => {
  const keywordA = makeKeywordA(locale)
  const keywordB = makeKeywordB(locale)
  const keywordC = makeKeywordC(locale)
  const statisticList = makeDemoCompetitiveMentionAnalysisKolAnalyzeList(locale)

  return {
    page: 1,
    per_page: 3,
    total: 3,
    total_page: 1,
    data: [
      {
        keyword_info: {
          ...keywordA,
          starred: keywordA.starred_keyword,
        },
        mention: statisticList.data[0].mention,
      },
      {
        keyword_info: {
          ...keywordB,
          starred: keywordB.starred_keyword,
        },
        mention: statisticList.data[1].mention,
      },
      {
        keyword_info: {
          ...keywordC,
          starred: keywordC.starred_keyword,
        },
        mention: statisticList.data[2].mention,
      },
    ],
  }
}

const getDemoCompetitiveKeywordEfficientTrendDetail = (
  id: number,
  trendDataDate: string,
  locale?: SupportedLanguages,
): CompetitiveKeywordEffectivenessTrendChartDetail => {
  const keywordA = makeKeywordA(locale)
  const keywordB = makeKeywordB(locale)
  const keywordC = makeKeywordC(locale)
  const trendDetailDataDateList = makeTrendDetailDataDateList(locale)

  const postInfoRecord = {
    [keywordA.id]: trendDetailDataDateList[0],
    [keywordB.id]: trendDetailDataDateList[1],
    [keywordC.id]: trendDetailDataDateList[2],
  }

  return {
    kol_uuid: '',
    kol_name: '',
    follower_count: 0,
    post_author_uuid: '',
    post_url: '',
    post_crawler_id: '',
    post_type: '',
    post_like_count: 0,
    post_comment_count: 0,
    post_view_count: 0,
    post_share_count: 0,
    post_social_volume_count: 1680,
    ...postInfoRecord[id]?.[trendDataDate],
  }
}

export const makeDemoMentionKolEstimatedCollaborationPriceChartData = (
  locale?: SupportedLanguages,
): FetchMentionKolEstimatedCollaborationPriceChartDataResponse => {
  const mentionedBrandA = makeMentionedBrandA(locale)
  const mentionedBrandB = makeMentionedBrandB(locale)
  const mentionedBrandC = makeMentionedBrandC(locale)

  return {
    estimated_collaboration_price_charts: [
      {
        ...unimportantAttrs,
        id: mentionedBrandA.kol_uuid,
        name: mentionedBrandA.name,
        starred_kol: false,
        estimated_collaboration_prices: [
          {
            follower_level: 'level1',
            kol_count: 1,
            percentage: 0.1111111111111111,
            price: '3500',
          },
          {
            follower_level: 'level2',
            kol_count: 5,
            percentage: 0.56,
            price: '62500',
          },
          {
            follower_level: 'level3',
            kol_count: 3,
            percentage: 0.33222222122,
            price: '93000',
          },
          {
            follower_level: 'level4',
            kol_count: null,
            percentage: null,
            price: null,
          },
          {
            follower_level: 'level5',
            kol_count: null,
            percentage: null,
            price: null,
          },
          {
            follower_level: 'level6',
            kol_count: null,
            percentage: null,
            price: null,
          },
          {
            follower_level: 'level7',
            kol_count: null,
            percentage: null,
            price: null,
          },
          {
            follower_level: 'level8',
            kol_count: null,
            percentage: null,
            price: null,
          },
        ],
      },
      {
        ...unimportantAttrs,
        id: mentionedBrandB.kol_uuid,
        name: mentionedBrandB.name,
        starred_kol: false,
        estimated_collaboration_prices: [
          {
            follower_level: 'level1',
            kol_count: 2,
            percentage: 0.6443636363636364,
            price: '7000',
          },
          {
            follower_level: 'level2',
            kol_count: 1,
            percentage: 0.36363636363636365,
            price: '12500',
          },
          {
            follower_level: 'level3',
            kol_count: null,
            percentage: null,
            price: null,
          },
          {
            follower_level: 'level4',
            kol_count: null,
            percentage: null,
            price: null,
          },
          {
            follower_level: 'level5',
            kol_count: null,
            percentage: null,
            price: null,
          },
          {
            follower_level: 'level6',
            kol_count: null,
            percentage: null,
            price: null,
          },
          {
            follower_level: 'level7',
            kol_count: null,
            percentage: null,
            price: null,
          },
          {
            follower_level: 'level8',
            kol_count: null,
            percentage: null,
            price: null,
          },
        ],
      },
      {
        ...unimportantAttrs,
        id: mentionedBrandC.kol_uuid,
        name: mentionedBrandC.name,
        starred_kol: false,
        estimated_collaboration_prices: [
          {
            follower_level: 'level1',
            kol_count: null,
            percentage: null,
            price: null,
          },
          {
            follower_level: 'level2',
            kol_count: 2,
            percentage: 0.503636363636,
            price: '25000',
          },
          {
            follower_level: 'level3',
            kol_count: null,
            percentage: null,
            price: null,
          },
          {
            follower_level: 'level4',
            kol_count: 2,
            percentage: 0.503636363,
            price: '103000',
          },
          {
            follower_level: 'level5',
            kol_count: null,
            percentage: null,
            price: null,
          },
          {
            follower_level: 'level6',
            kol_count: null,
            percentage: null,
            price: null,
          },
          {
            follower_level: 'level7',
            kol_count: null,
            percentage: null,
            price: null,
          },
          {
            follower_level: 'level8',
            kol_count: null,
            percentage: null,
            price: null,
          },
        ],
      },
    ],
  }
}

export const makeDemoMentionKeywordEstimatedCollaborationPriceChartData = (
  locale?: SupportedLanguages,
): FetchMentionKeywordEstimatedCollaborationPriceChartDataResponse => {
  const keywordA = makeKeywordA(locale)
  const keywordB = makeKeywordB(locale)
  const keywordC = makeKeywordC(locale)

  return {
    estimated_collaboration_price_charts: [
      {
        ...pick(keywordA, ['id', 'name', 'starred_keyword']),
        estimated_collaboration_prices: [
          {
            follower_level: 'level1',
            kol_count: 1,
            percentage: 0.1111111111111111,
            price: '3500',
          },
          {
            follower_level: 'level2',
            kol_count: 5,
            percentage: 0.56,
            price: '62500',
          },
          {
            follower_level: 'level3',
            kol_count: 3,
            percentage: 0.3322222222,
            price: '93000',
          },
          {
            follower_level: 'level4',
            kol_count: null,
            percentage: null,
            price: null,
          },
          {
            follower_level: 'level5',
            kol_count: null,
            percentage: null,
            price: null,
          },
          {
            follower_level: 'level6',
            kol_count: null,
            percentage: null,
            price: null,
          },
          {
            follower_level: 'level7',
            kol_count: null,
            percentage: null,
            price: null,
          },
          {
            follower_level: 'level8',
            kol_count: null,
            percentage: null,
            price: null,
          },
        ],
      },
      {
        ...pick(keywordB, ['id', 'name', 'starred_keyword']),
        estimated_collaboration_prices: [
          {
            follower_level: 'level1',
            kol_count: 2,
            percentage: 0.6443636363636364,
            price: '7000',
          },
          {
            follower_level: 'level2',
            kol_count: 1,
            percentage: 0.36363636363636365,
            price: '12500',
          },
          {
            follower_level: 'level3',
            kol_count: null,
            percentage: null,
            price: null,
          },
          {
            follower_level: 'level4',
            kol_count: null,
            percentage: null,
            price: null,
          },
          {
            follower_level: 'level5',
            kol_count: null,
            percentage: null,
            price: null,
          },
          {
            follower_level: 'level6',
            kol_count: null,
            percentage: null,
            price: null,
          },
          {
            follower_level: 'level7',
            kol_count: null,
            percentage: null,
            price: null,
          },
          {
            follower_level: 'level8',
            kol_count: null,
            percentage: null,
            price: null,
          },
        ],
      },
      {
        ...pick(keywordC, ['id', 'name', 'starred_keyword']),
        estimated_collaboration_prices: [
          {
            follower_level: 'level1',
            kol_count: null,
            percentage: null,
            price: null,
          },
          {
            follower_level: 'level2',
            kol_count: 2,
            percentage: 0.50363636,
            price: '25000',
          },
          {
            follower_level: 'level3',
            kol_count: null,
            percentage: null,
            price: null,
          },
          {
            follower_level: 'level4',
            kol_count: 2,
            percentage: 0.503636363,
            price: '103000',
          },
          {
            follower_level: 'level5',
            kol_count: null,
            percentage: null,
            price: null,
          },
          {
            follower_level: 'level6',
            kol_count: null,
            percentage: null,
            price: null,
          },
          {
            follower_level: 'level7',
            kol_count: null,
            percentage: null,
            price: null,
          },
          {
            follower_level: 'level8',
            kol_count: null,
            percentage: null,
            price: null,
          },
        ],
      },
    ],
  }
}

const demoCompetitiveKeywordAnalysis = DEMO_COMPETITIVE_KEYWORD_ANALYSIS

const demoCompetitiveKeywordKolCustomizedTagsNameChartData: FetchCompetitiveKeywordKolCustomizedTagsNameChartDataResponse =
  {
    mention_keyword_kol_customized_tags_name_charts:
      DEMO_COMPETITIVE_MENTION_KOL_PROPERTY_CHART_DATA.mention_kol_property_charts,
  }

const demoCompetitiveMentionAnalysis = DEMO_COMPETITIVE_MENTION_ANALYSIS

const demoCompetitiveMentionKolPropertyChartData =
  DEMO_COMPETITIVE_MENTION_KOL_PROPERTY_CHART_DATA

const demoCrawlerStatus = DEMO_CRAWLER_STATUS

export {
  DEMO_COMPETITIVE_ANALYSIS_ID,
  demoCompetitiveKeywordAnalysis,
  demoCompetitiveKeywordKolCustomizedTagsNameChartData,
  demoCompetitiveMentionAnalysis,
  demoCompetitiveMentionKolPropertyChartData,
  demoCrawlerStatus,
  getDemoCompetitiveKeywordEfficientTrendDetail,
  getDemoCompetitiveMentionedEfficientTrendDetail,
}
